import React from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../../shared/components';
import { useToast } from '../../notifications';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes } from '../../auth/permissions';
import {
  createKnowledgeDataCategory,
  createKnowledgeDynamicTool,
  deleteKnowledgeDataCategory,
  deleteKnowledgeDynamicTool,
  introspectKnowledgeJsonSchema,
  listKnowledgeDataCategoryOverview,
  listKnowledgeDynamicTools,
  previewKnowledgeDynamicTool,
  syncKnowledgeCategoryConsultation,
  syncKnowledgeCategoryExternal,
  getKnowledgeGoogleSheetsSettings,
  startKnowledgeGoogleSheetsTenantOAuth,
  disconnectKnowledgeGoogleSheetsAccount,
  syncKnowledgeCategoryGoogleSheets,
  updateKnowledgeGoogleSheetsSettings,
  updateKnowledgeDataCategory,
  updateKnowledgeDynamicTool,
  uploadKnowledgeCategoryCsv,
  uploadKnowledgeCategoryJson,
} from '../services/knowledgeService';
import {
  getConsultationTemplateById,
  listConsultationFieldDefinitions,
  listConsultationStatuses,
  listConsultationTemplates,
} from '../../consultations/services/consultationService';
import type { ConsultationFieldDefinition, ConsultationStatusDefinition } from '../../consultations/types/Consultation';
import {
  JsonSchemaField,
  KnowledgeDataCategoryOverview,
  KnowledgeDataSourceType,
  KnowledgeExternalSyncAuthType,
  KnowledgeDynamicTool,
  KnowledgeDynamicToolParameter,
  KnowledgeDynamicToolResponseField,
  KnowledgeGoogleSheetsSettingsResponse,
  PreviewKnowledgeDynamicToolResult,
} from '../types/Knowledge';
import {
  KNOWLEDGE_CONSULTATION_METADATA_DEFAULTS,
  KNOWLEDGE_CONSULTATION_METADATA_FIELDS,
  type KnowledgeConsultationMetadataField,
} from '../utils/knowledgeConsultationMetadata';
import {
  buildToolTemplateParameterPatches,
  buildToolTemplateResponseFieldPaths,
  buildToolTemplateSettings,
  ToolTemplateId,
} from '../utils/knowledgeDynamicToolTemplates';
import {
  buildDynamicToolPreviewSummary,
  getDynamicToolPreviewGroups,
  isUnaryMatchMode,
  mergePreviewArgumentsForExecution,
  syncPreviewArguments,
} from '../utils/knowledgeDynamicToolPreview';
import { ToolUseCaseAssignment } from '../../tools/components/ToolUseCaseAssignment';
import type { AiPromptUseCaseId } from '../../admin/types/AiPromptConfig';
import { DEFAULT_ASSIGNED_USE_CASE_IDS } from '../../tools/constants/assignableUseCases';
import { getToolUseCaseAssignments } from '../../tools/services/toolUseCaseAssignmentService';
import styles from './KnowledgeBasePage.module.css';

type PanelView = 'browse-categories' | 'browse-tools' | 'category-form' | 'tool-form';
type BrowseTab = 'categories' | 'tools';
type ToolParameterDraft = KnowledgeDynamicToolParameter & {
  selected: boolean;
  allowedValuesText?: string;
  draftId: string;
};
type ResponseFieldDraft = KnowledgeDynamicToolResponseField & {
  selected: boolean;
  draftId: string;
};

const createDraftId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `draft-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const parseAllowedValues = (raw: string): string[] =>
  raw
    .split(',')
    .map(value => value.trim())
    .filter(Boolean);

const formatAllowedValues = (values: string[] | undefined): string =>
  (values ?? []).join(', ');

const MATCH_MODES = [
  'contains',
  'equals',
  'startswith',
  'endswith',
  'numericequals',
  'numericgte',
  'numericlte',
  'dateequals',
  'datebefore',
  'dateafter',
  'in',
  'exists',
  'isempty',
] as const;

const PARAMETER_FORMATS = ['', 'text', 'date', 'email', 'id', 'number'] as const;

const DEFAULT_CATEGORY_SYNC_INTERVAL_MINUTES = 15;

const inferParameterFormat = (field: JsonSchemaField): string => {
  if (field.type === 'number') {
    return 'number';
  }

  if (field.jsonPath.toLowerCase().includes('date') || field.name.toLowerCase().includes('date')) {
    return 'date';
  }

  return 'text';
};

const slugifyCategoryId = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 128);

const toParameterDraft = (field: JsonSchemaField): ToolParameterDraft => ({
  draftId: createDraftId(),
  name: field.name.replace(/[^A-Za-z0-9_]/g, '_'),
  description: `Filter by ${field.name}. Sample values: ${field.sampleValues.slice(0, 3).join(', ') || field.type}.`,
  jsonPath: field.jsonPath,
  matchMode: 'contains',
  isRequired: false,
  allowedValues: field.sampleValues.slice(0, 5),
  allowedValuesText: field.sampleValues.slice(0, 5).join(', '),
  example: field.sampleValues[0] ?? '',
  format: inferParameterFormat(field),
  defaultValue: '',
  selected: true,
});

const toParameterDraftFromTool = (parameter: KnowledgeDynamicToolParameter): ToolParameterDraft => ({
  ...parameter,
  draftId: createDraftId(),
  selected: true,
  allowedValuesText: formatAllowedValues(parameter.allowedValues),
});

const createEmptyParameter = (): ToolParameterDraft => ({
  draftId: createDraftId(),
  name: '',
  description: '',
  jsonPath: '',
  matchMode: 'contains',
  isRequired: false,
  allowedValues: [],
  example: '',
  format: '',
  defaultValue: '',
  selected: true,
});

const mergeSchemaFields = (
  current: ToolParameterDraft[],
  incoming: ToolParameterDraft[],
): ToolParameterDraft[] => {
  const merged = [...current];
  const existingPaths = new Set(current.map(item => item.jsonPath.toLowerCase()));

  incoming.forEach((field) => {
    if (!existingPaths.has(field.jsonPath.toLowerCase())) {
      merged.push(field);
      existingPaths.add(field.jsonPath.toLowerCase());
    }
  });

  return merged;
};

const toResponseFieldDraft = (field: JsonSchemaField): ResponseFieldDraft => ({
  draftId: createDraftId(),
  name: field.name.replace(/[^A-Za-z0-9_]/g, '_'),
  jsonPath: field.jsonPath,
  selected: false,
});

const toResponseFieldDraftFromTool = (field: KnowledgeDynamicToolResponseField): ResponseFieldDraft => ({
  ...field,
  draftId: createDraftId(),
  selected: true,
});

const createEmptyResponseField = (): ResponseFieldDraft => ({
  draftId: createDraftId(),
  name: '',
  jsonPath: '',
  selected: true,
});

const mergeResponseFields = (
  current: ResponseFieldDraft[],
  incoming: ResponseFieldDraft[],
): ResponseFieldDraft[] => {
  const merged = [...current];
  const existingPaths = new Set(current.map(item => item.jsonPath.toLowerCase()));

  incoming.forEach((field) => {
    if (!existingPaths.has(field.jsonPath.toLowerCase())) {
      merged.push(field);
      existingPaths.add(field.jsonPath.toLowerCase());
    }
  });

  return merged;
};

const GOOGLE_CLOUD_CREDENTIALS_URL = 'https://console.cloud.google.com/apis/credentials';
const GOOGLE_SHEETS_OAUTH_CALLBACK_PATH = '/api/v1/knowledge/google-sheets/oauth/callback';
const GOOGLE_SHEETS_OAUTH_FRONTEND_PATH = '/ai/tools/data';

const getApiBaseUrl = () => (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

const getDefaultGoogleSheetsOAuthRedirectUri = () => {
  const apiBase = getApiBaseUrl();
  return apiBase ? `${apiBase}${GOOGLE_SHEETS_OAUTH_CALLBACK_PATH}` : '';
};

const isFrontendOriginUrl = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  try {
    return new URL(trimmed).origin === window.location.origin;
  } catch {
    return false;
  }
};

const getDefaultGoogleSheetsOAuthFrontendReturnUrl = () => GOOGLE_SHEETS_OAUTH_FRONTEND_PATH;

const looksLikeEmailAddress = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const toRelativeGoogleSheetsFrontendReturnUrl = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return GOOGLE_SHEETS_OAUTH_FRONTEND_PATH;
  }

  try {
    if (/^https?:\/\//i.test(trimmed)) {
      const parsed = new URL(trimmed);
      return `${parsed.pathname}${parsed.search}` || GOOGLE_SHEETS_OAUTH_FRONTEND_PATH;
    }
  } catch {
    return GOOGLE_SHEETS_OAUTH_FRONTEND_PATH;
  }

  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

type KnowledgeDynamicToolsPanelProps = {
  showPageHeader?: boolean;
};

export const KnowledgeDynamicToolsPanel: React.FC<KnowledgeDynamicToolsPanelProps> = ({
  showPageHeader = true,
}) => {
  const { t } = useTranslation(['knowledge', 'common']);
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canManageKnowledge = hasPermission([PermissionCodes.knowledgeManage]);
  const canSyncKnowledge = hasPermission([PermissionCodes.knowledgeSync, PermissionCodes.knowledgeManage]);

  const [loading, setLoading] = React.useState(true);
  const [view, setView] = React.useState<PanelView>('browse-categories');
  const [categoryOverviews, setCategoryOverviews] = React.useState<KnowledgeDataCategoryOverview[]>([]);
  const [tools, setTools] = React.useState<KnowledgeDynamicTool[]>([]);

  const [editingCategoryId, setEditingCategoryId] = React.useState<string | null>(null);
  const [categoryIdDraft, setCategoryIdDraft] = React.useState('');
  const [categoryDisplayName, setCategoryDisplayName] = React.useState('');
  const [categoryDescription, setCategoryDescription] = React.useState('');
  const [categoryDataRootPath, setCategoryDataRootPath] = React.useState('');
  const [categoryExternalSyncUrl, setCategoryExternalSyncUrl] = React.useState('');
  const [categoryExternalSyncTitle, setCategoryExternalSyncTitle] = React.useState('');
  const [categoryExternalSyncIntervalMinutes, setCategoryExternalSyncIntervalMinutes] = React.useState(0);
  const [categoryExternalSyncScheduleEnabled, setCategoryExternalSyncScheduleEnabled] = React.useState(false);
  const [categoryExternalSyncAuthType, setCategoryExternalSyncAuthType] = React.useState<KnowledgeExternalSyncAuthType>('none');
  const [categoryExternalSyncAuthHeaderName, setCategoryExternalSyncAuthHeaderName] = React.useState('X-API-Key');
  const [categoryExternalSyncAuthValue, setCategoryExternalSyncAuthValue] = React.useState('');
  const [clearExternalSyncAuthValue, setClearExternalSyncAuthValue] = React.useState(false);
  const [categoryExternalSyncAuthConfigured, setCategoryExternalSyncAuthConfigured] = React.useState(false);
  const [categoryDataSourceType, setCategoryDataSourceType] = React.useState<KnowledgeDataSourceType>('document');
  const [consultationTemplates, setConsultationTemplates] = React.useState<Array<{ id: string; name: string }>>([]);
  const [consultationTemplateId, setConsultationTemplateId] = React.useState('');
  const [consultationFieldDefinitionId, setConsultationFieldDefinitionId] = React.useState('');
  const [consultationFieldOptions, setConsultationFieldOptions] = React.useState<ConsultationFieldDefinition[]>([]);
  const [browseConsultationFieldDefinitions, setBrowseConsultationFieldDefinitions] = React.useState<ConsultationFieldDefinition[]>([]);
  const [consultationFlattenRepeater, setConsultationFlattenRepeater] = React.useState(true);
  const [consultationSyncIntervalMinutes, setConsultationSyncIntervalMinutes] = React.useState(0);
  const [consultationSyncScheduleEnabled, setConsultationSyncScheduleEnabled] = React.useState(false);
  const [consultationIncludeStatuses, setConsultationIncludeStatuses] = React.useState<string[]>([]);
  const [consultationAdditionalFieldDefinitionIds, setConsultationAdditionalFieldDefinitionIds] = React.useState<string[]>([]);
  const [consultationAutoSyncOnConsultationChange, setConsultationAutoSyncOnConsultationChange] = React.useState(true);
  const [consultationAutoSyncOnFieldValueChange, setConsultationAutoSyncOnFieldValueChange] = React.useState(true);
  const [googleSheetsSpreadsheetId, setGoogleSheetsSpreadsheetId] = React.useState('');
  const [googleSheetsSheetGid, setGoogleSheetsSheetGid] = React.useState('0');
  const [googleSheetsSyncIntervalMinutes, setGoogleSheetsSyncIntervalMinutes] = React.useState(0);
  const [googleSheetsSyncScheduleEnabled, setGoogleSheetsSyncScheduleEnabled] = React.useState(false);
  const [googleSheetsAuthType, setGoogleSheetsAuthType] = React.useState<'public' | 'service_account' | 'oauth'>('public');
  const [googleSheetsAuthValue, setGoogleSheetsAuthValue] = React.useState('');
  const [clearGoogleSheetsAuthValue, setClearGoogleSheetsAuthValue] = React.useState(false);
  const [googleSheetsAuthConfigured, setGoogleSheetsAuthConfigured] = React.useState(false);
  const [googleSheetsTenantOAuthClientId, setGoogleSheetsTenantOAuthClientId] = React.useState('');
  const [googleSheetsTenantOAuthClientSecret, setGoogleSheetsTenantOAuthClientSecret] = React.useState('');
  const [googleSheetsTenantOAuthRedirectUri, setGoogleSheetsTenantOAuthRedirectUri] = React.useState('');
  const [googleSheetsTenantOAuthFrontendReturnUrl, setGoogleSheetsTenantOAuthFrontendReturnUrl] = React.useState('');
  const [googleSheetsTenantOAuthFrontendReturnUrlResolved, setGoogleSheetsTenantOAuthFrontendReturnUrlResolved] = React.useState('');
  const [googleSheetsTenantOAuthSecretConfigured, setGoogleSheetsTenantOAuthSecretConfigured] = React.useState(false);
  const [clearGoogleSheetsTenantOAuthSecret, setClearGoogleSheetsTenantOAuthSecret] = React.useState(false);
  const [googleSheetsTenantSettingsOpen, setGoogleSheetsTenantSettingsOpen] = React.useState(false);
  const [googleSheetsTenantOAuthSecretVisible, setGoogleSheetsTenantOAuthSecretVisible] = React.useState(false);
  const [copiedGoogleSheetsField, setCopiedGoogleSheetsField] = React.useState<'redirect' | 'return' | null>(null);
  const [loadingGoogleSheetsTenantSettings, setLoadingGoogleSheetsTenantSettings] = React.useState(false);
  const [savingGoogleSheetsTenantSettings, setSavingGoogleSheetsTenantSettings] = React.useState(false);
  const [googleSheetsTenantSettingsHydrated, setGoogleSheetsTenantSettingsHydrated] = React.useState(false);
  const [googleSheetsOAuthAppConfigured, setGoogleSheetsOAuthAppConfigured] = React.useState(false);
  const [googleSheetsOAuthAppManagedByPlatform, setGoogleSheetsOAuthAppManagedByPlatform] = React.useState(false);
  const [googleSheetsGoogleAccountConnected, setGoogleSheetsGoogleAccountConnected] = React.useState(false);
  const [connectingGoogleSheetsTenantOAuth, setConnectingGoogleSheetsTenantOAuth] = React.useState(false);
  const [disconnectingGoogleSheetsAccount, setDisconnectingGoogleSheetsAccount] = React.useState(false);
  const googleSheetsTenantAutoOpenedRef = React.useRef(false);
  const [consultationStatusOptions, setConsultationStatusOptions] = React.useState<ConsultationStatusDefinition[]>([]);
  const [consultationIncludeMetadataFields, setConsultationIncludeMetadataFields] = React.useState<KnowledgeConsultationMetadataField[]>(
    [...KNOWLEDGE_CONSULTATION_METADATA_DEFAULTS],
  );
  const [creatingCategory, setCreatingCategory] = React.useState(false);
  const [syncingCategoryId, setSyncingCategoryId] = React.useState<string | null>(null);
  const [uploadingCategoryId, setUploadingCategoryId] = React.useState<string | null>(null);
  const categoryUploadInputRef = React.useRef<HTMLInputElement | null>(null);
  const [pendingUploadCategoryId, setPendingUploadCategoryId] = React.useState<string | null>(null);
  const [categoryFormUploadFile, setCategoryFormUploadFile] = React.useState<File | null>(null);
  const [categoryFormUploadTitle, setCategoryFormUploadTitle] = React.useState('');
  const [categoryFormUploading, setCategoryFormUploading] = React.useState(false);
  const categoryFormUploadInputRef = React.useRef<HTMLInputElement | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = React.useState<ToolTemplateId>('');
  const [assignedUseCaseIds, setAssignedUseCaseIds] = React.useState<AiPromptUseCaseId[]>(
    [...DEFAULT_ASSIGNED_USE_CASE_IDS],
  );

  const [editingToolId, setEditingToolId] = React.useState<string | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = React.useState('');
  const [toolDataRootPath, setToolDataRootPath] = React.useState('');
  const [kernelFunctionName, setKernelFunctionName] = React.useState('');
  const [toolDisplayName, setToolDisplayName] = React.useState('');
  const [toolDescription, setToolDescription] = React.useState('');
  const [responseInstruction, setResponseInstruction] = React.useState('');
  const [maxResults, setMaxResults] = React.useState(100);
  const [sortJsonPath, setSortJsonPath] = React.useState('');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');
  const [filterLogic, setFilterLogic] = React.useState<'and' | 'or'>('and');
  const [resultMode, setResultMode] = React.useState<'list' | 'count' | 'groupby'>('list');
  const [groupByJsonPath, setGroupByJsonPath] = React.useState('');
  const [isPublished, setIsPublished] = React.useState(true);
  const [knowledgeSearchParameter, setKnowledgeSearchParameter] = React.useState('');
  const [knowledgeSearchMaxChunks, setKnowledgeSearchMaxChunks] = React.useState(3);
  const [schemaFields, setSchemaFields] = React.useState<ToolParameterDraft[]>([]);
  const [responseFields, setResponseFields] = React.useState<ResponseFieldDraft[]>([]);
  const [previewArguments, setPreviewArguments] = React.useState<Record<string, string>>({});
  const [previewResult, setPreviewResult] = React.useState<PreviewKnowledgeDynamicToolResult | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [introspecting, setIntrospecting] = React.useState(false);
  const [savingTool, setSavingTool] = React.useState(false);

  const isEditingTool = Boolean(editingToolId);
  const isEditingCategory = Boolean(editingCategoryId);
  const categories = categoryOverviews;

  const resetCategoryForm = React.useCallback(() => {
    setEditingCategoryId(null);
    setCategoryIdDraft('');
    setCategoryDisplayName('');
    setCategoryDescription('');
    setCategoryDataRootPath('');
    setCategoryExternalSyncUrl('');
    setCategoryExternalSyncTitle('');
    setCategoryExternalSyncIntervalMinutes(0);
    setCategoryExternalSyncScheduleEnabled(false);
    setCategoryExternalSyncAuthType('none');
    setCategoryExternalSyncAuthHeaderName('X-API-Key');
    setCategoryExternalSyncAuthValue('');
    setClearExternalSyncAuthValue(false);
    setCategoryExternalSyncAuthConfigured(false);
    setCategoryDataSourceType('document');
    setConsultationTemplateId('');
    setConsultationFieldDefinitionId('');
    setConsultationFieldOptions([]);
    setConsultationFlattenRepeater(true);
    setConsultationSyncIntervalMinutes(0);
    setConsultationSyncScheduleEnabled(false);
    setConsultationIncludeStatuses([]);
    setConsultationAdditionalFieldDefinitionIds([]);
    setConsultationAutoSyncOnConsultationChange(true);
    setConsultationAutoSyncOnFieldValueChange(true);
    setGoogleSheetsSpreadsheetId('');
    setGoogleSheetsSheetGid('0');
    setGoogleSheetsSyncIntervalMinutes(0);
    setGoogleSheetsSyncScheduleEnabled(false);
    setGoogleSheetsAuthType('public');
    setGoogleSheetsAuthValue('');
    setClearGoogleSheetsAuthValue(false);
    setGoogleSheetsAuthConfigured(false);
    setConsultationIncludeMetadataFields([...KNOWLEDGE_CONSULTATION_METADATA_DEFAULTS]);
    setCategoryFormUploadFile(null);
    setCategoryFormUploadTitle('');
    setCategoryFormUploading(false);
    if (categoryFormUploadInputRef.current) {
      categoryFormUploadInputRef.current.value = '';
    }
  }, []);

  const resetToolForm = React.useCallback((categoryId?: string) => {
    setEditingToolId(null);
    setSelectedCategoryId(categoryId ?? categories[0]?.categoryId ?? '');
    setToolDataRootPath('');
    setKernelFunctionName('');
    setToolDisplayName('');
    setToolDescription('');
    setResponseInstruction('');
    setMaxResults(100);
    setSortJsonPath('');
    setSortDirection('asc');
    setFilterLogic('and');
    setResultMode('list');
    setGroupByJsonPath('');
    setIsPublished(true);
    setKnowledgeSearchParameter('');
    setKnowledgeSearchMaxChunks(3);
    setSchemaFields([]);
    setResponseFields([]);
    setPreviewArguments({});
    setPreviewResult(null);
    setSelectedTemplateId('');
    setAssignedUseCaseIds([...DEFAULT_ASSIGNED_USE_CASE_IDS]);
  }, [categories]);

  const loadData = React.useCallback(async () => {
    setLoading(true);
    try {
      const [nextOverviews, nextTools] = await Promise.all([
        listKnowledgeDataCategoryOverview(),
        listKnowledgeDynamicTools(),
      ]);
      setCategoryOverviews(nextOverviews);
      setTools(nextTools);
    } catch (loadError) {
      showToast(loadError instanceof Error ? loadError.message : t('dynamicTools.failedToLoad', { ns: 'knowledge' }), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  const applyGoogleSheetsTenantSettings = React.useCallback((settings: KnowledgeGoogleSheetsSettingsResponse) => {
    const redirectUri = settings.oauthRedirectUri.trim() || getDefaultGoogleSheetsOAuthRedirectUri();
    const frontendReturnUrl = toRelativeGoogleSheetsFrontendReturnUrl(
      settings.oauthFrontendReturnUrl || GOOGLE_SHEETS_OAUTH_FRONTEND_PATH);
    setGoogleSheetsTenantOAuthClientId(settings.oauthClientId);
    setGoogleSheetsTenantOAuthRedirectUri(redirectUri);
    setGoogleSheetsTenantOAuthFrontendReturnUrl(frontendReturnUrl);
    setGoogleSheetsTenantOAuthFrontendReturnUrlResolved(settings.oauthFrontendReturnUrlResolved.trim());
    setGoogleSheetsTenantOAuthSecretConfigured(settings.oauthClientSecretConfigured);
    setGoogleSheetsTenantOAuthClientSecret('');
    setClearGoogleSheetsTenantOAuthSecret(false);
    setGoogleSheetsTenantOAuthSecretVisible(false);
    setGoogleSheetsOAuthAppConfigured(settings.oauthAppConfigured);
    setGoogleSheetsOAuthAppManagedByPlatform(settings.oauthAppManagedByPlatform);
    setGoogleSheetsGoogleAccountConnected(settings.googleAccountConnected);
  }, []);

  const loadGoogleSheetsTenantSettings = React.useCallback(async () => {
    setLoadingGoogleSheetsTenantSettings(true);
    try {
      const settings = await getKnowledgeGoogleSheetsSettings();
      applyGoogleSheetsTenantSettings(settings);

      setGoogleSheetsTenantSettingsHydrated(true);
      if (!googleSheetsTenantAutoOpenedRef.current) {
        googleSheetsTenantAutoOpenedRef.current = true;
        setGoogleSheetsTenantSettingsOpen(!settings.oauthAppConfigured && !settings.googleAccountConnected);
      }
    } catch (settingsError) {
      showToast(
        settingsError instanceof Error
          ? settingsError.message
          : t('dynamicTools.googleSheetsTenantSettingsLoadFailed', { ns: 'knowledge' }),
        'error',
      );
      setGoogleSheetsTenantSettingsHydrated(true);
      if (!googleSheetsTenantAutoOpenedRef.current) {
        googleSheetsTenantAutoOpenedRef.current = true;
        setGoogleSheetsTenantOAuthRedirectUri(getDefaultGoogleSheetsOAuthRedirectUri());
        setGoogleSheetsTenantOAuthFrontendReturnUrl(getDefaultGoogleSheetsOAuthFrontendReturnUrl());
        setGoogleSheetsTenantSettingsOpen(true);
      }
    } finally {
      setLoadingGoogleSheetsTenantSettings(false);
    }
  }, [applyGoogleSheetsTenantSettings, showToast, t]);

  React.useEffect(() => {
    void loadData();
  }, [loadData]);

  React.useEffect(() => {
    void loadGoogleSheetsTenantSettings();
  }, [loadGoogleSheetsTenantSettings]);

  React.useEffect(() => {
    if (!copiedGoogleSheetsField) {
      return;
    }

    const timer = window.setTimeout(() => setCopiedGoogleSheetsField(null), 1600);
    return () => window.clearTimeout(timer);
  }, [copiedGoogleSheetsField]);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthStatus = params.get('googleSheetsOAuth');
    if (!oauthStatus) {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.delete('googleSheetsOAuth');
    url.searchParams.delete('categoryId');
    window.history.replaceState({}, '', url.toString());

    if (oauthStatus === 'success') {
      showToast(t('dynamicTools.googleSheetsOAuthSuccess', { ns: 'knowledge' }), 'success');
      void loadData();
      return;
    }

    showToast(t('dynamicTools.googleSheetsOAuthFailed', { ns: 'knowledge' }), 'error');
  }, [loadData, showToast, t]);

  React.useEffect(() => {
    if (view !== 'tool-form' || isEditingTool || toolDataRootPath.trim()) {
      return;
    }

    const category = categories.find(item => item.categoryId === selectedCategoryId);
    if (category?.dataRootPath) {
      setToolDataRootPath(category.dataRootPath);
    }
  }, [categories, isEditingTool, selectedCategoryId, toolDataRootPath, view]);

  const selectedPreviewParameters = React.useMemo(
    () => schemaFields.filter(item => item.selected && item.name.trim()),
    [schemaFields],
  );

  React.useEffect(() => {
    setPreviewArguments(current => syncPreviewArguments(selectedPreviewParameters, current));
  }, [selectedPreviewParameters]);

  const resolveConsultationTemplateName = React.useCallback((templateId?: string) => {
    if (!templateId) {
      return '—';
    }

    return consultationTemplates.find((template) => template.id === templateId)?.name ?? templateId;
  }, [consultationTemplates]);

  const resolveConsultationFieldName = React.useCallback((fieldDefinitionId?: string) => {
    if (!fieldDefinitionId) {
      return '—';
    }

    return browseConsultationFieldDefinitions.find((definition) => definition.id === fieldDefinitionId)?.name
      ?? consultationFieldOptions.find((definition) => definition.id === fieldDefinitionId)?.name
      ?? fieldDefinitionId;
  }, [browseConsultationFieldDefinitions, consultationFieldOptions]);

  React.useEffect(() => {
    if (view !== 'category-form' && view !== 'browse-categories') {
      return;
    }

    void listConsultationTemplates({ isActive: true })
      .then((templates) => {
        setConsultationTemplates(templates.map((template) => ({ id: template.id, name: template.name })));
      })
      .catch(() => {
        setConsultationTemplates([]);
      });
  }, [view]);

  React.useEffect(() => {
    if (view !== 'browse-categories') {
      return;
    }

    void listConsultationFieldDefinitions({ isActive: true })
      .then((definitions) => {
        setBrowseConsultationFieldDefinitions(definitions);
      })
      .catch(() => {
        setBrowseConsultationFieldDefinitions([]);
      });
  }, [view]);

  React.useEffect(() => {
    if (view !== 'category-form' || !consultationTemplateId) {
      setConsultationFieldOptions([]);
      return;
    }

    void (async () => {
      const [template, definitions] = await Promise.all([
        getConsultationTemplateById(consultationTemplateId),
        listConsultationFieldDefinitions({ isActive: true }),
      ]);

      const assignedIds = new Set((template?.fields ?? []).map((field) => field.fieldDefinitionId));
      setConsultationFieldOptions(definitions.filter((definition) => assignedIds.has(definition.id)));
    })().catch(() => {
      setConsultationFieldOptions([]);
    });
  }, [consultationTemplateId, view]);

  const buildCategoryPayload = React.useCallback(() => {
    const includeStatuses = consultationIncludeStatuses.filter(Boolean);

    if (categoryDataSourceType === 'google_sheets') {
      return {
        displayName: categoryDisplayName.trim(),
        description: categoryDescription.trim() || undefined,
        dataRootPath: categoryDataRootPath.trim() || 'items',
        dataSourceType: 'google_sheets' as const,
        googleSheetsSpreadsheetId: googleSheetsSpreadsheetId.trim() || undefined,
        googleSheetsSheetGid: googleSheetsSheetGid.trim() || undefined,
        googleSheetsSyncIntervalMinutes: googleSheetsSyncScheduleEnabled
          ? (googleSheetsSyncIntervalMinutes > 0 ? googleSheetsSyncIntervalMinutes : DEFAULT_CATEGORY_SYNC_INTERVAL_MINUTES)
          : 0,
        googleSheetsAuthType,
        googleSheetsAuthValue: googleSheetsAuthType === 'service_account'
          ? (googleSheetsAuthValue.trim() || undefined)
          : undefined,
        clearGoogleSheetsAuthValue: isEditingCategory && clearGoogleSheetsAuthValue ? true : undefined,
      };
    }

    if (categoryDataSourceType === 'csv') {
      return {
        displayName: categoryDisplayName.trim(),
        description: categoryDescription.trim() || undefined,
        dataRootPath: categoryDataRootPath.trim() || 'items',
        dataSourceType: 'csv' as const,
        externalSyncUrl: categoryExternalSyncUrl.trim() || undefined,
        externalSyncTitle: categoryExternalSyncTitle.trim() || undefined,
        externalSyncIntervalMinutes: categoryExternalSyncUrl.trim()
          ? (categoryExternalSyncScheduleEnabled
            ? (categoryExternalSyncIntervalMinutes > 0 ? categoryExternalSyncIntervalMinutes : DEFAULT_CATEGORY_SYNC_INTERVAL_MINUTES)
            : 0)
          : undefined,
        externalSyncAuthType: categoryExternalSyncUrl.trim() ? categoryExternalSyncAuthType : 'none',
        externalSyncAuthHeaderName: categoryExternalSyncUrl.trim() && categoryExternalSyncAuthType === 'api_key_header'
          ? (categoryExternalSyncAuthHeaderName.trim() || 'X-API-Key')
          : undefined,
        externalSyncAuthValue: categoryExternalSyncAuthValue.trim() || undefined,
        clearExternalSyncAuthValue: isEditingCategory && clearExternalSyncAuthValue ? true : undefined,
      };
    }

    if (categoryDataSourceType === 'consultation') {
      return {
        displayName: categoryDisplayName.trim(),
        description: categoryDescription.trim() || undefined,
        dataRootPath: categoryDataRootPath.trim() || 'items',
        dataSourceType: 'consultation' as const,
        consultationTemplateId: consultationTemplateId || undefined,
        consultationFieldDefinitionId: consultationFieldDefinitionId || undefined,
        consultationFlattenRepeater,
        consultationSyncIntervalMinutes: consultationSyncScheduleEnabled
          ? (consultationSyncIntervalMinutes > 0 ? consultationSyncIntervalMinutes : DEFAULT_CATEGORY_SYNC_INTERVAL_MINUTES)
          : 0,
        consultationIncludeStatuses: includeStatuses.length > 0 ? includeStatuses : undefined,
        consultationIncludeMetadataFields,
        consultationAdditionalFieldDefinitionIds: consultationAdditionalFieldDefinitionIds.length > 0
          ? consultationAdditionalFieldDefinitionIds
          : undefined,
        consultationAutoSyncOnConsultationChange,
        consultationAutoSyncOnFieldValueChange,
      };
    }

    return {
      displayName: categoryDisplayName.trim(),
      description: categoryDescription.trim() || undefined,
      dataRootPath: categoryDataRootPath.trim() || undefined,
      dataSourceType: 'document' as const,
      externalSyncUrl: categoryExternalSyncUrl.trim() || undefined,
      externalSyncTitle: categoryExternalSyncTitle.trim() || undefined,
      externalSyncIntervalMinutes: categoryExternalSyncUrl.trim()
        ? (categoryExternalSyncScheduleEnabled
          ? (categoryExternalSyncIntervalMinutes > 0 ? categoryExternalSyncIntervalMinutes : DEFAULT_CATEGORY_SYNC_INTERVAL_MINUTES)
          : 0)
        : undefined,
      externalSyncAuthType: categoryExternalSyncUrl.trim() ? categoryExternalSyncAuthType : 'none',
      externalSyncAuthHeaderName: categoryExternalSyncUrl.trim() && categoryExternalSyncAuthType === 'api_key_header'
        ? (categoryExternalSyncAuthHeaderName.trim() || 'X-API-Key')
        : undefined,
      externalSyncAuthValue: categoryExternalSyncAuthValue.trim() || undefined,
      clearExternalSyncAuthValue: isEditingCategory && clearExternalSyncAuthValue ? true : undefined,
    };
  }, [
    categoryDataRootPath,
    categoryDataSourceType,
    categoryDescription,
    categoryDisplayName,
    categoryExternalSyncAuthHeaderName,
    categoryExternalSyncAuthType,
    categoryExternalSyncAuthValue,
    categoryExternalSyncIntervalMinutes,
    categoryExternalSyncScheduleEnabled,
    categoryExternalSyncTitle,
    categoryExternalSyncUrl,
    clearExternalSyncAuthValue,
    isEditingCategory,
    consultationFieldDefinitionId,
    consultationFlattenRepeater,
    consultationIncludeStatuses,
    consultationIncludeMetadataFields,
    consultationAdditionalFieldDefinitionIds,
    consultationAutoSyncOnConsultationChange,
    consultationAutoSyncOnFieldValueChange,
    consultationSyncIntervalMinutes,
    consultationSyncScheduleEnabled,
    consultationTemplateId,
    googleSheetsSpreadsheetId,
    googleSheetsSheetGid,
    googleSheetsSyncIntervalMinutes,
    googleSheetsSyncScheduleEnabled,
    googleSheetsAuthType,
    googleSheetsAuthValue,
    clearGoogleSheetsAuthValue,
  ]);

  const goToBrowse = (tab: BrowseTab = 'categories') => {
    setView(tab === 'tools' ? 'browse-tools' : 'browse-categories');
    setEditingToolId(null);
    setEditingCategoryId(null);
  };

  const openCreateCategory = () => {
    resetCategoryForm();
    setView('category-form');
  };

  const openEditCategory = (category: KnowledgeDataCategoryOverview) => {
    setEditingCategoryId(category.categoryId);
    setCategoryIdDraft(category.categoryId);
    setCategoryDisplayName(category.displayName);
    setCategoryDescription(category.description);
    setCategoryDataRootPath(category.dataRootPath);
    setCategoryExternalSyncUrl(category.externalSyncUrl ?? '');
    setCategoryExternalSyncTitle(category.externalSyncTitle ?? '');
    setCategoryExternalSyncIntervalMinutes(category.externalSyncIntervalMinutes ?? 0);
    setCategoryExternalSyncScheduleEnabled((category.externalSyncIntervalMinutes ?? 0) > 0);
    setCategoryExternalSyncAuthType(category.externalSyncAuthType ?? 'none');
    setCategoryExternalSyncAuthHeaderName(category.externalSyncAuthHeaderName || 'X-API-Key');
    setCategoryExternalSyncAuthValue('');
    setClearExternalSyncAuthValue(false);
    setCategoryExternalSyncAuthConfigured(category.externalSyncAuthConfigured);
    setCategoryDataSourceType(category.dataSourceType ?? 'document');
    setConsultationTemplateId(category.consultationSource?.templateId ?? '');
    setConsultationFieldDefinitionId(category.consultationSource?.fieldDefinitionId ?? '');
    setConsultationFlattenRepeater(category.consultationSource?.flattenRepeater ?? true);
    setConsultationSyncIntervalMinutes(category.consultationSource?.syncIntervalMinutes ?? 0);
    setConsultationSyncScheduleEnabled((category.consultationSource?.syncIntervalMinutes ?? 0) > 0);
    setConsultationIncludeStatuses(category.consultationSource?.includeStatuses ?? []);
    setConsultationAdditionalFieldDefinitionIds(category.consultationSource?.additionalFieldDefinitionIds ?? []);
    setConsultationAutoSyncOnConsultationChange(category.consultationSource?.autoSyncOnConsultationChange ?? true);
    setConsultationAutoSyncOnFieldValueChange(category.consultationSource?.autoSyncOnFieldValueChange ?? true);
    setGoogleSheetsSpreadsheetId(category.googleSheetsSource?.spreadsheetId ?? '');
    setGoogleSheetsSheetGid(category.googleSheetsSource?.sheetGid ?? '0');
    setGoogleSheetsSyncIntervalMinutes(category.googleSheetsSource?.syncIntervalMinutes ?? 0);
    setGoogleSheetsSyncScheduleEnabled((category.googleSheetsSource?.syncIntervalMinutes ?? 0) > 0);
    setGoogleSheetsAuthType(category.googleSheetsSource?.authType ?? 'public');
    setGoogleSheetsAuthValue('');
    setClearGoogleSheetsAuthValue(false);
    setGoogleSheetsAuthConfigured(category.googleSheetsSource?.authConfigured ?? false);
    setConsultationIncludeMetadataFields(
      (category.consultationSource?.includeMetadataFields?.length
        ? category.consultationSource.includeMetadataFields
        : KNOWLEDGE_CONSULTATION_METADATA_DEFAULTS) as KnowledgeConsultationMetadataField[],
    );
    setView('category-form');
  };

  React.useEffect(() => {
    if (view !== 'category-form') {
      return;
    }

    void listConsultationStatuses()
      .then((statuses) => setConsultationStatusOptions(statuses))
      .catch(() => setConsultationStatusOptions([]));
  }, [view]);

  const additionalConsultationFieldOptions = React.useMemo(
    () => consultationFieldOptions.filter((field) => field.id !== consultationFieldDefinitionId),
    [consultationFieldDefinitionId, consultationFieldOptions],
  );

  const toggleConsultationAdditionalField = (fieldId: string) => {
    setConsultationAdditionalFieldDefinitionIds((current) => (
      current.includes(fieldId)
        ? current.filter((item) => item !== fieldId)
        : [...current, fieldId]
    ));
  };

  const toggleConsultationStatus = (statusName: string) => {
    setConsultationIncludeStatuses((current) => (
      current.includes(statusName)
        ? current.filter((item) => item !== statusName)
        : [...current, statusName]
    ));
  };

  const toggleConsultationMetadataField = (field: KnowledgeConsultationMetadataField) => {
    setConsultationIncludeMetadataFields((current) => (
      current.includes(field)
        ? current.filter((item) => item !== field)
        : [...current, field]
    ));
  };

  const applySelectedTemplate = React.useCallback((
    templateId: ToolTemplateId,
    fields: JsonSchemaField[],
    parameterDrafts: ToolParameterDraft[],
    responseDrafts: ResponseFieldDraft[],
  ) => {
    if (!templateId) {
      return {
        parameters: parameterDrafts,
        responseFields: responseDrafts,
      };
    }

    const category = categories.find(item => item.categoryId === selectedCategoryId);
    const settings = buildToolTemplateSettings(templateId, category?.displayName ?? selectedCategoryId, fields);
    const parameterPatches = buildToolTemplateParameterPatches(templateId, fields);
    const responseFieldPaths = buildToolTemplateResponseFieldPaths(templateId, fields);

    if (settings) {
      setKernelFunctionName(settings.kernelFunctionName);
      setToolDisplayName(settings.toolDisplayName);
      setToolDescription(settings.toolDescription);
      setMaxResults(settings.maxResults);
      setSortJsonPath(settings.sortJsonPath);
      setSortDirection(settings.sortDirection);
      setResponseInstruction(settings.responseInstruction);
    }

    const patchedParameters = parameterDrafts.map((parameter) => {
      const patch = parameterPatches.find(item => item.jsonPath === parameter.jsonPath);
      return patch
        ? { ...parameter, selected: patch.selected, matchMode: patch.matchMode, isRequired: patch.isRequired }
        : { ...parameter, selected: false };
    });

    const patchedResponseFields = responseDrafts.map((field) => ({
      ...field,
      selected: responseFieldPaths.length === 0 || responseFieldPaths.includes(field.jsonPath),
    }));

    return {
      parameters: patchedParameters,
      responseFields: patchedResponseFields,
    };
  }, [categories, selectedCategoryId]);

  const handleTemplateChange = (nextTemplateId: ToolTemplateId) => {
    setSelectedTemplateId(nextTemplateId);
    if (schemaFields.length === 0) {
      return;
    }

    const fields: JsonSchemaField[] = schemaFields.map(item => ({
      jsonPath: item.jsonPath,
      name: item.name,
      type: item.format === 'number' ? 'number' : 'string',
      sampleValues: item.allowedValues ?? [],
    }));
    const templated = applySelectedTemplate(nextTemplateId, fields, schemaFields, responseFields);
    setSchemaFields(templated.parameters);
    setResponseFields(templated.responseFields);
  };

  const openCreateTool = (categoryId?: string) => {
    resetToolForm(categoryId ?? categories[0]?.categoryId ?? '');
    setView('tool-form');
  };

  const onCreateCategory = async () => {
    const normalizedCategoryId = slugifyCategoryId(categoryIdDraft || categoryDisplayName);
    if (!normalizedCategoryId || !categoryDisplayName.trim()) {
      showToast(t('dynamicTools.categoryValidation', { ns: 'knowledge' }), 'error');
      return;
    }

    if (categoryDataSourceType === 'consultation') {
      if (!consultationTemplateId || !consultationFieldDefinitionId) {
        showToast(t('dynamicTools.consultationSourceValidation', { ns: 'knowledge' }), 'error');
        return;
      }
    }

    if (categoryDataSourceType === 'google_sheets' && !googleSheetsSpreadsheetId.trim()) {
      showToast(t('dynamicTools.googleSheetsSourceValidation', { ns: 'knowledge' }), 'error');
      return;
    }

    if (categoryDataSourceType === 'google_sheets' && googleSheetsAuthType === 'service_account') {
      const hasStoredAuth = isEditingCategory && googleSheetsAuthConfigured && !clearGoogleSheetsAuthValue;
      if (!googleSheetsAuthValue.trim() && !hasStoredAuth) {
        showToast(t('dynamicTools.googleSheetsAuthValidation', { ns: 'knowledge' }), 'error');
        return;
      }
    }

    if ((categoryDataSourceType === 'document' || categoryDataSourceType === 'csv') && categoryExternalSyncUrl.trim()) {
      const requiresAuthValue = categoryExternalSyncAuthType === 'bearer' || categoryExternalSyncAuthType === 'api_key_header';
      const hasAuthValue = categoryExternalSyncAuthValue.trim().length > 0;
      const hasStoredAuth = isEditingCategory && categoryExternalSyncAuthConfigured && !clearExternalSyncAuthValue;
      if (requiresAuthValue && !hasAuthValue && !hasStoredAuth) {
        showToast(t('dynamicTools.externalSyncAuthValidation', { ns: 'knowledge' }), 'error');
        return;
      }
    }

    setCreatingCategory(true);
    try {
      const payload = buildCategoryPayload();
      const targetCategoryId = isEditingCategory && editingCategoryId
        ? editingCategoryId
        : normalizedCategoryId;

      if (isEditingCategory && editingCategoryId) {
        await updateKnowledgeDataCategory(editingCategoryId, payload);
        showToast(t('dynamicTools.categoryUpdated', { ns: 'knowledge' }), 'success');
      } else {
        await createKnowledgeDataCategory({
          categoryId: normalizedCategoryId,
          ...payload,
        });
        showToast(t('dynamicTools.categoryCreated', { ns: 'knowledge' }), 'success');
      }

      if (
        categoryFormUploadFile
        && (categoryDataSourceType === 'document' || categoryDataSourceType === 'csv')
      ) {
        try {
          await uploadCategoryDataFile(
            targetCategoryId,
            categoryFormUploadFile,
            categoryDataSourceType,
            categoryFormUploadTitle.trim() || undefined,
          );
        } catch (uploadError) {
          showToast(
            uploadError instanceof Error
              ? uploadError.message
              : t(categoryDataSourceType === 'csv' ? 'dynamicTools.failedToUploadCategoryCsv' : 'dynamicTools.failedToUploadCategoryJson', { ns: 'knowledge' }),
            'error',
          );
          await loadData();
          return;
        }
      }

      resetCategoryForm();
      await loadData();
      setView('browse-categories');
    } catch (createError) {
      showToast(
        createError instanceof Error
          ? createError.message
          : t(isEditingCategory ? 'dynamicTools.failedToUpdateCategory' : 'dynamicTools.failedToCreateCategory', { ns: 'knowledge' }),
        'error',
      );
    } finally {
      setCreatingCategory(false);
    }
  };

  const onSyncCategoryExternal = async (category: KnowledgeDataCategoryOverview) => {
    setSyncingCategoryId(category.categoryId);
    try {
      const result = await syncKnowledgeCategoryExternal(category.categoryId);
      showToast(
        t(category.dataSourceType === 'csv' ? 'dynamicTools.externalCsvSyncSuccess' : 'dynamicTools.externalSyncSuccess', {
          ns: 'knowledge',
          title: result.title || category.displayName,
        }),
        'success',
      );
      await loadData();
    } catch (syncError) {
      showToast(
        syncError instanceof Error
          ? syncError.message
          : t(category.dataSourceType === 'csv' ? 'dynamicTools.failedToSyncExternalCsv' : 'dynamicTools.failedToSyncExternal', { ns: 'knowledge' }),
        'error',
      );
    } finally {
      setSyncingCategoryId(null);
    }
  };

  const uploadCategoryDataFile = async (
    categoryId: string,
    file: File,
    dataSourceType: KnowledgeDataSourceType,
    title?: string,
  ) => {
    const isCsvCategory = dataSourceType === 'csv';
    const result = isCsvCategory
      ? await uploadKnowledgeCategoryCsv(categoryId, file, title)
      : await uploadKnowledgeCategoryJson(categoryId, file, title);

    showToast(
      t(isCsvCategory ? 'dynamicTools.categoryCsvUploadSuccess' : 'dynamicTools.categoryUploadSuccess', {
        ns: 'knowledge',
        title: result.title,
      }),
      'success',
    );

    return result;
  };

  const onUploadCategoryFile = async (categoryId: string, file: File) => {
    const category = categories.find((item) => item.categoryId === categoryId);
    const dataSourceType = category?.dataSourceType ?? 'document';

    setUploadingCategoryId(categoryId);
    try {
      await uploadCategoryDataFile(categoryId, file, dataSourceType);
      await loadData();
    } catch (uploadError) {
      showToast(
        uploadError instanceof Error
          ? uploadError.message
          : t(dataSourceType === 'csv' ? 'dynamicTools.failedToUploadCategoryCsv' : 'dynamicTools.failedToUploadCategoryJson', { ns: 'knowledge' }),
        'error',
      );
    } finally {
      setUploadingCategoryId(null);
      setPendingUploadCategoryId(null);
      if (categoryUploadInputRef.current) {
        categoryUploadInputRef.current.value = '';
      }
    }
  };

  const onUploadCategoryFormFile = async () => {
    if (!isEditingCategory || !editingCategoryId || !categoryFormUploadFile) {
      return;
    }

    if (categoryDataSourceType !== 'document' && categoryDataSourceType !== 'csv') {
      return;
    }

    setCategoryFormUploading(true);
    try {
      await uploadCategoryDataFile(
        editingCategoryId,
        categoryFormUploadFile,
        categoryDataSourceType,
        categoryFormUploadTitle.trim() || undefined,
      );
      setCategoryFormUploadFile(null);
      setCategoryFormUploadTitle('');
      if (categoryFormUploadInputRef.current) {
        categoryFormUploadInputRef.current.value = '';
      }
      await loadData();
    } catch (uploadError) {
      showToast(
        uploadError instanceof Error
          ? uploadError.message
          : t(categoryDataSourceType === 'csv' ? 'dynamicTools.failedToUploadCategoryCsv' : 'dynamicTools.failedToUploadCategoryJson', { ns: 'knowledge' }),
        'error',
      );
    } finally {
      setCategoryFormUploading(false);
    }
  };

  const clearCategoryFormUpload = () => {
    setCategoryFormUploadFile(null);
    setCategoryFormUploadTitle('');
    if (categoryFormUploadInputRef.current) {
      categoryFormUploadInputRef.current.value = '';
    }
  };

  const onChooseCategoryFormUpload = () => {
    categoryFormUploadInputRef.current?.click();
  };

  const onCategoryFormUploadSelected = (file: File | null) => {
    if (!file) {
      clearCategoryFormUpload();
      return;
    }

    const isCsvCategory = categoryDataSourceType === 'csv';
    const fileName = file.name.toLowerCase();
    const isValidFile = isCsvCategory
      ? fileName.endsWith('.csv') || file.type.includes('csv')
      : fileName.endsWith('.json') || file.type.includes('json');

    if (!isValidFile) {
      showToast(
        t(isCsvCategory ? 'dynamicTools.categoryFormInvalidCsvFile' : 'dynamicTools.categoryFormInvalidJsonFile', { ns: 'knowledge' }),
        'error',
      );
      clearCategoryFormUpload();
      return;
    }

    setCategoryFormUploadFile(file);
    if (!categoryFormUploadTitle.trim()) {
      setCategoryFormUploadTitle(file.name.replace(/\.[^.]+$/, ''));
    }
  };

  const onChooseCategoryUpload = (categoryId: string) => {
    setPendingUploadCategoryId(categoryId);
    categoryUploadInputRef.current?.click();
  };

  const googleSheetsTenantClientIdLooksLikeEmail = looksLikeEmailAddress(googleSheetsTenantOAuthClientId);
  const googleSheetsTenantOAuthStatus = googleSheetsGoogleAccountConnected
    ? 'ready'
    : googleSheetsOAuthAppConfigured
      ? 'needs-connect'
      : googleSheetsTenantClientIdLooksLikeEmail
        ? 'check-id'
        : 'needs-setup';
  const googleSheetsTenantOAuthBusy = loadingGoogleSheetsTenantSettings
    || savingGoogleSheetsTenantSettings
    || connectingGoogleSheetsTenantOAuth
    || disconnectingGoogleSheetsAccount;

  const copyGoogleSheetsOAuthValue = async (value: string, field: 'redirect' | 'return') => {
    if (!value.trim() || !navigator.clipboard?.writeText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(value.trim());
      setCopiedGoogleSheetsField(field);
    } catch {
      showToast(t('dynamicTools.googleSheetsTenantOAuthCopyFailed', { ns: 'knowledge' }), 'error');
    }
  };

  const onSaveGoogleSheetsTenantSettings = async () => {
    const clientId = googleSheetsTenantOAuthClientId.trim();
    const redirectUri = googleSheetsTenantOAuthRedirectUri.trim() || getDefaultGoogleSheetsOAuthRedirectUri();
    const apiRedirectUri = redirectUri && !isFrontendOriginUrl(redirectUri) ? redirectUri : undefined;
    const frontendReturnUrl = toRelativeGoogleSheetsFrontendReturnUrl(
      googleSheetsTenantOAuthFrontendReturnUrl || GOOGLE_SHEETS_OAUTH_FRONTEND_PATH);

    if (!clientId) {
      showToast(t('dynamicTools.googleSheetsTenantOAuthClientIdRequired', { ns: 'knowledge' }), 'error');
      return;
    }

    if (looksLikeEmailAddress(clientId)) {
      showToast(t('dynamicTools.googleSheetsTenantOAuthClientIdEmailWarning', { ns: 'knowledge' }), 'error');
      return;
    }

    if (!googleSheetsTenantOAuthSecretConfigured && !googleSheetsTenantOAuthClientSecret.trim()) {
      showToast(t('dynamicTools.googleSheetsTenantOAuthClientSecretRequired', { ns: 'knowledge' }), 'error');
      return;
    }

    setSavingGoogleSheetsTenantSettings(true);
    try {
      const settings = await updateKnowledgeGoogleSheetsSettings({
        oauthClientId: clientId,
        oauthClientSecret: googleSheetsTenantOAuthClientSecret.trim() || undefined,
        oauthRedirectUri: apiRedirectUri,
        oauthFrontendReturnUrl: frontendReturnUrl,
        clearOAuthClientSecret: clearGoogleSheetsTenantOAuthSecret ? true : undefined,
      });
      applyGoogleSheetsTenantSettings(settings);
      showToast(t('dynamicTools.googleSheetsTenantSettingsSaved', { ns: 'knowledge' }), 'success');
    } catch (saveError) {
      showToast(
        saveError instanceof Error
          ? saveError.message
          : t('dynamicTools.googleSheetsTenantSettingsSaveFailed', { ns: 'knowledge' }),
        'error',
      );
    } finally {
      setSavingGoogleSheetsTenantSettings(false);
    }
  };

  const onConnectGoogleSheetsTenantOAuth = async () => {
    let oauthAppConfigured = googleSheetsOAuthAppConfigured;
    if (!oauthAppConfigured) {
      try {
        const settings = await getKnowledgeGoogleSheetsSettings();
        applyGoogleSheetsTenantSettings(settings);
        oauthAppConfigured = settings.oauthAppConfigured;
      } catch {
        oauthAppConfigured = false;
      }
    }

    if (!oauthAppConfigured) {
      showToast(t('dynamicTools.googleSheetsOAuthAppMissing', { ns: 'knowledge' }), 'error');
      setGoogleSheetsTenantSettingsOpen(true);
      return;
    }

    setConnectingGoogleSheetsTenantOAuth(true);
    try {
      const result = await startKnowledgeGoogleSheetsTenantOAuth();
      if (!result.authorizeUrl) {
        throw new Error(t('dynamicTools.googleSheetsOAuthFailed', { ns: 'knowledge' }));
      }
      window.location.assign(result.authorizeUrl);
    } catch (connectError) {
      showToast(
        connectError instanceof Error
          ? connectError.message
          : t('dynamicTools.googleSheetsOAuthFailed', { ns: 'knowledge' }),
        'error',
      );
      setConnectingGoogleSheetsTenantOAuth(false);
    }
  };

  const onDisconnectGoogleSheetsAccount = async () => {
    setDisconnectingGoogleSheetsAccount(true);
    try {
      const settings = await disconnectKnowledgeGoogleSheetsAccount();
      setGoogleSheetsOAuthAppConfigured(settings.oauthAppConfigured);
      setGoogleSheetsOAuthAppManagedByPlatform(settings.oauthAppManagedByPlatform);
      setGoogleSheetsGoogleAccountConnected(settings.googleAccountConnected);
      setGoogleSheetsTenantOAuthSecretConfigured(settings.oauthClientSecretConfigured);
      showToast(t('dynamicTools.googleSheetsOAuthDisconnected', { ns: 'knowledge' }), 'success');
    } catch (disconnectError) {
      showToast(
        disconnectError instanceof Error
          ? disconnectError.message
          : t('dynamicTools.googleSheetsOAuthDisconnectFailed', { ns: 'knowledge' }),
        'error',
      );
    } finally {
      setDisconnectingGoogleSheetsAccount(false);
    }
  };

  const onSyncCategoryGoogleSheets = async (category: KnowledgeDataCategoryOverview) => {
    setSyncingCategoryId(category.categoryId);
    try {
      const result = await syncKnowledgeCategoryGoogleSheets(category.categoryId);
      showToast(
        t('dynamicTools.googleSheetsSyncSuccess', {
          ns: 'knowledge',
          title: result.title || category.displayName,
        }),
        'success',
      );
      await loadData();
    } catch (syncError) {
      showToast(
        syncError instanceof Error ? syncError.message : t('dynamicTools.failedToSyncGoogleSheets', { ns: 'knowledge' }),
        'error',
      );
    } finally {
      setSyncingCategoryId(null);
    }
  };

  const onSyncCategoryConsultation = async (category: KnowledgeDataCategoryOverview) => {
    setSyncingCategoryId(category.categoryId);
    try {
      const result = await syncKnowledgeCategoryConsultation(category.categoryId);
      showToast(
        t('dynamicTools.consultationSyncSuccess', {
          ns: 'knowledge',
          title: result.title || category.displayName,
        }),
        'success',
      );
      await loadData();
    } catch (syncError) {
      showToast(
        syncError instanceof Error ? syncError.message : t('dynamicTools.failedToSyncConsultation', { ns: 'knowledge' }),
        'error',
      );
    } finally {
      setSyncingCategoryId(null);
    }
  };

  const onDeleteCategory = async (category: KnowledgeDataCategoryOverview) => {
    if (!window.confirm(t('dynamicTools.deleteCategoryConfirm', { ns: 'knowledge', name: category.displayName }))) {
      return;
    }

    try {
      await deleteKnowledgeDataCategory(category.categoryId);
      showToast(t('dynamicTools.categoryDeleted', { ns: 'knowledge' }), 'success');
      await loadData();
    } catch (deleteError) {
      showToast(deleteError instanceof Error ? deleteError.message : t('dynamicTools.failedToDeleteCategory', { ns: 'knowledge' }), 'error');
    }
  };

  const onIntrospectSchema = async () => {
    if (!selectedCategoryId) {
      showToast(t('dynamicTools.selectCategoryFirst', { ns: 'knowledge' }), 'error');
      return;
    }

    setIntrospecting(true);
    try {
      const result = await introspectKnowledgeJsonSchema({
        categoryId: selectedCategoryId,
        dataRootPath: toolDataRootPath.trim() || undefined,
      });
      const incomingFields = result.fields.map(toParameterDraft);
      const incomingResponseFields = result.fields.map(toResponseFieldDraft);
      const mergedParameters = mergeSchemaFields(schemaFields, incomingFields);
      const mergedResponseFields = mergeResponseFields(responseFields, incomingResponseFields);
      const templated = applySelectedTemplate(selectedTemplateId, result.fields, mergedParameters, mergedResponseFields);
      setSchemaFields(templated.parameters);
      setResponseFields(templated.responseFields);
      if (result.dataRootPath) {
        setToolDataRootPath(result.dataRootPath);
      }
      showToast(
        result.isSynthetic
          ? t('dynamicTools.schemaLoadedSynthetic', { ns: 'knowledge', count: incomingFields.length })
          : t('dynamicTools.schemaLoaded', { ns: 'knowledge', count: incomingFields.length }),
        'success',
      );
    } catch (introspectError) {
      showToast(introspectError instanceof Error ? introspectError.message : t('dynamicTools.failedToIntrospect', { ns: 'knowledge' }), 'error');
    } finally {
      setIntrospecting(false);
    }
  };

  const onEditTool = (tool: KnowledgeDynamicTool) => {
    setEditingToolId(tool.toolId);
    setSelectedCategoryId(tool.categoryId);
    setToolDataRootPath(tool.dataRootPath);
    setKernelFunctionName(tool.kernelFunctionName);
    setToolDisplayName(tool.displayName);
    setToolDescription(tool.description);
    setResponseInstruction(tool.responseInstruction);
    setMaxResults(tool.maxResults);
    setSortJsonPath(tool.sortJsonPath);
    setSortDirection(tool.sortDirection === 'desc' ? 'desc' : 'asc');
    setFilterLogic(tool.filterLogic === 'or' ? 'or' : 'and');
    setResultMode(
      tool.resultMode === 'count' || tool.resultMode === 'groupby' ? tool.resultMode : 'list',
    );
    setGroupByJsonPath(tool.groupByJsonPath);
    setIsPublished(tool.isPublished);
    setKnowledgeSearchParameter(tool.knowledgeSearchParameter);
    setKnowledgeSearchMaxChunks(tool.knowledgeSearchMaxChunks);
    setSchemaFields(tool.parameters.map(toParameterDraftFromTool));
    setResponseFields(tool.responseFields.map(toResponseFieldDraftFromTool));
    setPreviewArguments(
      syncPreviewArguments(
        tool.parameters.map(toParameterDraftFromTool),
        Object.fromEntries(tool.parameters.map(parameter => [parameter.name, parameter.defaultValue ?? ''])),
      ),
    );
    setPreviewResult(null);
    setAssignedUseCaseIds([]);
    void getToolUseCaseAssignments(tool.functionId)
      .then(setAssignedUseCaseIds)
      .catch(() => setAssignedUseCaseIds([]));
    setView('tool-form');
  };

  const buildToolPayload = () => {
    const selectedParameters = schemaFields.filter(item => item.selected);
    const selectedResponseFields = responseFields.filter(item => item.selected);
    const normalizedParameters = selectedParameters.map(({ selected: _selected, allowedValuesText, draftId: _draftId, ...parameter }) => ({
        ...parameter,
        name: parameter.name.trim(),
        description: parameter.description.trim(),
        jsonPath: parameter.jsonPath.trim(),
        allowedValues: parseAllowedValues(allowedValuesText ?? formatAllowedValues(parameter.allowedValues)),
        example: parameter.example?.trim() || undefined,
        format: parameter.format?.trim() || undefined,
        defaultValue: parameter.defaultValue?.trim() || undefined,
      }));

    return {
      kernelFunctionName: kernelFunctionName.trim(),
      displayName: toolDisplayName.trim(),
      description: toolDescription.trim(),
      dataRootPath: toolDataRootPath.trim() || undefined,
      parameters: normalizedParameters,
      responseFields: selectedResponseFields.map(({ selected: _selected, draftId: _draftId, ...field }) => ({
        name: field.name.trim(),
        jsonPath: field.jsonPath.trim(),
      })),
      responseInstruction: responseInstruction.trim() || undefined,
      maxResults: maxResults > 0 ? maxResults : 100,
      sortJsonPath: sortJsonPath.trim() || undefined,
      sortDirection,
      filterLogic,
      resultMode,
      groupByJsonPath: resultMode === 'groupby' ? groupByJsonPath.trim() || undefined : undefined,
      isPublished,
      knowledgeSearchParameter: knowledgeSearchParameter.trim() || undefined,
      knowledgeSearchMaxChunks: knowledgeSearchParameter.trim()
        ? knowledgeSearchMaxChunks
        : undefined,
      assignedUseCaseIds,
    };
  };

  const onPreviewTool = async () => {
    const payload = buildToolPayload();
    if (!selectedCategoryId) {
      showToast(t('dynamicTools.previewValidation', { ns: 'knowledge' }), 'error');
      return;
    }

    setPreviewLoading(true);
    try {
      const previewArgs = mergePreviewArgumentsForExecution(payload.parameters, previewArguments);
      const result = await previewKnowledgeDynamicTool({
        tool: {
          categoryId: selectedCategoryId,
          dataRootPath: payload.dataRootPath,
          parameters: payload.parameters,
          responseFields: payload.responseFields,
          responseInstruction: payload.responseInstruction,
          maxResults: payload.maxResults,
          sortJsonPath: payload.sortJsonPath,
          sortDirection: payload.sortDirection,
          filterLogic: payload.filterLogic,
          resultMode: payload.resultMode,
          groupByJsonPath: payload.groupByJsonPath,
          knowledgeSearchParameter: payload.knowledgeSearchParameter,
          knowledgeSearchMaxChunks: payload.knowledgeSearchMaxChunks,
        },
        arguments: previewArgs,
      });
      setPreviewResult(result);
    } catch (previewError) {
      showToast(previewError instanceof Error ? previewError.message : t('dynamicTools.failedToPreview', { ns: 'knowledge' }), 'error');
    } finally {
      setPreviewLoading(false);
    }
  };

  const onSaveTool = async () => {
    const payload = buildToolPayload();
    if (!payload.kernelFunctionName || !payload.displayName || !payload.description) {
      showToast(t('dynamicTools.toolValidation', { ns: 'knowledge' }), 'error');
      return;
    }

    if (!selectedCategoryId) {
      showToast(t('dynamicTools.toolValidation', { ns: 'knowledge' }), 'error');
      return;
    }

    const invalidParameter = payload.parameters.find(item =>
      !item.name || !item.jsonPath || !item.description);
    if (invalidParameter) {
      showToast(t('dynamicTools.parameterFieldValidation', { ns: 'knowledge' }), 'error');
      return;
    }

    const invalidResponseField = payload.responseFields?.find(item => !item.name || !item.jsonPath);
    if (invalidResponseField) {
      showToast(t('dynamicTools.responseFieldValidation', { ns: 'knowledge' }), 'error');
      return;
    }

    setSavingTool(true);
    try {
      if (isEditingTool && editingToolId) {
        await updateKnowledgeDynamicTool(editingToolId, payload);
        showToast(t('dynamicTools.toolUpdated', { ns: 'knowledge' }), 'success');
      } else {
        await createKnowledgeDynamicTool({
          categoryId: selectedCategoryId,
          ...payload,
        });
        showToast(t('dynamicTools.toolCreated', { ns: 'knowledge' }), 'success');
      }

      resetToolForm(selectedCategoryId);
      setView('browse-tools');
      await loadData();
    } catch (saveError) {
      showToast(
        saveError instanceof Error
          ? saveError.message
          : t(isEditingTool ? 'dynamicTools.failedToUpdateTool' : 'dynamicTools.failedToCreateTool', { ns: 'knowledge' }),
        'error',
      );
    } finally {
      setSavingTool(false);
    }
  };

  const onDeleteTool = async (tool: KnowledgeDynamicTool) => {
    if (!window.confirm(t('dynamicTools.deleteToolConfirm', { ns: 'knowledge', name: tool.displayName }))) {
      return;
    }

    try {
      await deleteKnowledgeDynamicTool(tool.toolId);
      if (editingToolId === tool.toolId) {
        resetToolForm();
        setView('browse-tools');
      }
      showToast(t('dynamicTools.toolDeleted', { ns: 'knowledge' }), 'success');
      await loadData();
    } catch (deleteError) {
      showToast(deleteError instanceof Error ? deleteError.message : t('dynamicTools.failedToDeleteTool', { ns: 'knowledge' }), 'error');
    }
  };

  const updateParameter = (index: number, patch: Partial<ToolParameterDraft>) => {
    setSchemaFields(current => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, ...patch } : item
    )));
  };

  const removeParameter = (index: number) => {
    setSchemaFields(current => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const updateResponseField = (index: number, patch: Partial<ResponseFieldDraft>) => {
    setResponseFields(current => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, ...patch } : item
    )));
  };

  const removeResponseField = (index: number) => {
    setResponseFields(current => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const getCategoryLabel = (categoryId: string) =>
    categories.find(item => item.categoryId === categoryId)?.displayName ?? categoryId;

  if (loading) {
    return (
      <div className={styles.loadingRow}>
        <Spinner size={16} />
        <span>{t('dynamicTools.loading', { ns: 'knowledge' })}</span>
      </div>
    );
  }

  if (view === 'category-form') {
    return (
      <div className={styles.dynamicToolsLayout}>
        <section className={`company-section ${styles.dynamicToolsEditorSection}`}>
          <div className={styles.dynamicToolsEditorHeader}>
            <div>
              <button type="button" className={styles.dynamicToolsBackLink} onClick={() => goToBrowse('categories')}>
                {t('dynamicTools.backToCategories', { ns: 'knowledge' })}
              </button>
              <h3>
                {isEditingCategory
                  ? t('dynamicTools.editCategoryTitle', { ns: 'knowledge' })
                  : t('dynamicTools.createCategoryTitle', { ns: 'knowledge' })}
              </h3>
              <p className={styles.pageIntro}>{t('dynamicTools.categoriesHelp', { ns: 'knowledge' })}</p>
            </div>
          </div>

          <div className={styles.dynamicToolsFormGrid}>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('dynamicTools.categoryId', { ns: 'knowledge' })}</span>
              <input
                className={styles.textField}
                value={categoryIdDraft}
                disabled={isEditingCategory}
                onChange={(event) => setCategoryIdDraft(event.target.value)}
                placeholder={t('dynamicTools.categoryIdPlaceholder', { ns: 'knowledge' })}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('dynamicTools.categoryDisplayName', { ns: 'knowledge' })}</span>
              <input
                className={styles.textField}
                value={categoryDisplayName}
                onChange={(event) => setCategoryDisplayName(event.target.value)}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('dynamicTools.dataRootPath', { ns: 'knowledge' })}</span>
              <input
                className={styles.textField}
                value={categoryDataRootPath}
                onChange={(event) => setCategoryDataRootPath(event.target.value)}
                placeholder={t('dynamicTools.dataRootPathPlaceholder', { ns: 'knowledge' })}
              />
            </label>
            <label className={`${styles.field} ${styles.fieldWide}`}>
              <span className={styles.fieldLabel}>{t('summary', { ns: 'knowledge' })}</span>
              <input
                className={styles.textField}
                value={categoryDescription}
                onChange={(event) => setCategoryDescription(event.target.value)}
              />
            </label>

            <div className={`${styles.field} ${styles.fieldWide}`}>
              <span className={styles.fieldLabel}>{t('dynamicTools.dataSourceType', { ns: 'knowledge' })}</span>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="radio"
                    name="categoryDataSourceType"
                    checked={categoryDataSourceType === 'document'}
                    onChange={() => {
                      setCategoryDataSourceType('document');
                      clearCategoryFormUpload();
                    }}
                  />
                  <span>{t('dynamicTools.dataSourceDocument', { ns: 'knowledge' })}</span>
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="radio"
                    name="categoryDataSourceType"
                    checked={categoryDataSourceType === 'csv'}
                    onChange={() => {
                      setCategoryDataSourceType('csv');
                      clearCategoryFormUpload();
                      if (!categoryDataRootPath.trim()) {
                        setCategoryDataRootPath('items');
                      }
                    }}
                  />
                  <span>{t('dynamicTools.dataSourceCsv', { ns: 'knowledge' })}</span>
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="radio"
                    name="categoryDataSourceType"
                    checked={categoryDataSourceType === 'google_sheets'}
                    onChange={() => {
                      setCategoryDataSourceType('google_sheets');
                      if (!categoryDataRootPath.trim()) {
                        setCategoryDataRootPath('items');
                      }
                      setGoogleSheetsAuthType('oauth');
                    }}
                  />
                  <span>{t('dynamicTools.dataSourceGoogleSheets', { ns: 'knowledge' })}</span>
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="radio"
                    name="categoryDataSourceType"
                    checked={categoryDataSourceType === 'consultation'}
                    onChange={() => {
                      setCategoryDataSourceType('consultation');
                      if (!categoryDataRootPath.trim()) {
                        setCategoryDataRootPath('items');
                      }
                    }}
                  />
                  <span>{t('dynamicTools.dataSourceConsultation', { ns: 'knowledge' })}</span>
                </label>
              </div>
            </div>

            {categoryDataSourceType === 'document' || categoryDataSourceType === 'csv' ? (
              <>
                <label className={`${styles.field} ${styles.fieldWide}`}>
                  <span className={styles.fieldLabel}>
                    {t(categoryDataSourceType === 'csv' ? 'dynamicTools.externalCsvSyncUrl' : 'dynamicTools.externalSyncUrl', { ns: 'knowledge' })}
                  </span>
                  <input
                    className={styles.textField}
                    value={categoryExternalSyncUrl}
                    onChange={(event) => setCategoryExternalSyncUrl(event.target.value)}
                    placeholder={t(
                      categoryDataSourceType === 'csv'
                        ? 'dynamicTools.externalCsvSyncUrlPlaceholder'
                        : 'dynamicTools.externalSyncUrlPlaceholder',
                      { ns: 'knowledge' },
                    )}
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('dynamicTools.externalSyncTitle', { ns: 'knowledge' })}</span>
                  <input
                    className={styles.textField}
                    value={categoryExternalSyncTitle}
                    onChange={(event) => setCategoryExternalSyncTitle(event.target.value)}
                    placeholder={t('dynamicTools.externalSyncTitlePlaceholder', { ns: 'knowledge' })}
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('dynamicTools.externalSyncAuthType', { ns: 'knowledge' })}</span>
                  <select
                    className={styles.selectField}
                    value={categoryExternalSyncAuthType}
                    disabled={!categoryExternalSyncUrl.trim()}
                    onChange={(event) => setCategoryExternalSyncAuthType(event.target.value as KnowledgeExternalSyncAuthType)}
                  >
                    <option value="none">{t('dynamicTools.externalSyncAuthNone', { ns: 'knowledge' })}</option>
                    <option value="bearer">{t('dynamicTools.externalSyncAuthBearer', { ns: 'knowledge' })}</option>
                    <option value="api_key_header">{t('dynamicTools.externalSyncAuthApiKey', { ns: 'knowledge' })}</option>
                  </select>
                </label>
                {categoryExternalSyncAuthType === 'api_key_header' ? (
                  <label className={styles.field}>
                    <span className={styles.fieldLabel}>{t('dynamicTools.externalSyncAuthHeaderName', { ns: 'knowledge' })}</span>
                    <input
                      className={styles.textField}
                      value={categoryExternalSyncAuthHeaderName}
                      disabled={!categoryExternalSyncUrl.trim()}
                      onChange={(event) => setCategoryExternalSyncAuthHeaderName(event.target.value)}
                      placeholder="X-API-Key"
                    />
                  </label>
                ) : null}
                {categoryExternalSyncAuthType !== 'none' ? (
                  <label className={`${styles.field} ${styles.fieldWide}`}>
                    <span className={styles.fieldLabel}>{t('dynamicTools.externalSyncAuthValue', { ns: 'knowledge' })}</span>
                    <input
                      className={styles.textField}
                      type="password"
                      value={categoryExternalSyncAuthValue}
                      disabled={!categoryExternalSyncUrl.trim()}
                      onChange={(event) => setCategoryExternalSyncAuthValue(event.target.value)}
                      placeholder={t('dynamicTools.externalSyncAuthValuePlaceholder', { ns: 'knowledge' })}
                    />
                    {isEditingCategory ? (
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                        <input
                          type="checkbox"
                          checked={clearExternalSyncAuthValue}
                          onChange={(event) => setClearExternalSyncAuthValue(event.target.checked)}
                        />
                        <span>{t('dynamicTools.externalSyncAuthClear', { ns: 'knowledge' })}</span>
                      </label>
                    ) : null}
                  </label>
                ) : null}
                <label className={`${styles.field} ${styles.fieldWide}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={categoryExternalSyncScheduleEnabled}
                    disabled={!categoryExternalSyncUrl.trim()}
                    onChange={(event) => {
                      const enabled = event.target.checked;
                      setCategoryExternalSyncScheduleEnabled(enabled);
                      if (enabled && categoryExternalSyncIntervalMinutes <= 0) {
                        setCategoryExternalSyncIntervalMinutes(DEFAULT_CATEGORY_SYNC_INTERVAL_MINUTES);
                      }
                    }}
                  />
                  <span>{t('dynamicTools.enableBackgroundSync', { ns: 'knowledge', defaultValue: 'Enable scheduled background sync' })}</span>
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('dynamicTools.externalSyncIntervalMinutes', { ns: 'knowledge' })}</span>
                  <input
                    className={styles.textField}
                    type="number"
                    min={5}
                    max={10080}
                    value={categoryExternalSyncIntervalMinutes}
                    disabled={!categoryExternalSyncUrl.trim() || !categoryExternalSyncScheduleEnabled}
                    onChange={(event) => setCategoryExternalSyncIntervalMinutes(Number(event.target.value) || 0)}
                  />
                </label>
                <p className={styles.mutedText}>
                  {t(categoryDataSourceType === 'csv' ? 'dynamicTools.externalCsvSyncHelp' : 'dynamicTools.externalSyncHelp', { ns: 'knowledge' })}
                </p>
                {categoryDataSourceType === 'csv' ? (
                  <p className={styles.mutedText}>{t('dynamicTools.csvSourceHelp', { ns: 'knowledge' })}</p>
                ) : null}
                <p className={styles.mutedText}>{t('dynamicTools.externalSyncIntervalHelp', { ns: 'knowledge' })}</p>

                <div className={`${styles.field} ${styles.fieldWide}`}>
                  <span className={styles.fieldLabel}>
                    {categoryDataSourceType === 'csv'
                      ? t('dynamicTools.categoryFormUploadCsv', { ns: 'knowledge' })
                      : t('dynamicTools.categoryFormUploadJson', { ns: 'knowledge' })}
                  </span>
                  <input
                    ref={categoryFormUploadInputRef}
                    type="file"
                    accept={categoryDataSourceType === 'csv' ? '.csv,text/csv' : '.json,application/json'}
                    style={{ display: 'none' }}
                    onChange={(event) => onCategoryFormUploadSelected(event.target.files?.[0] ?? null)}
                  />
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={onChooseCategoryFormUpload}
                      disabled={categoryFormUploading || creatingCategory}
                    >
                      {t('dynamicTools.chooseCategoryFile', { ns: 'knowledge' })}
                    </button>
                    {categoryFormUploadFile ? (
                      <>
                        <span>{categoryFormUploadFile.name}</span>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={clearCategoryFormUpload}
                          disabled={categoryFormUploading || creatingCategory}
                        >
                          {t('dynamicTools.clearCategoryFile', { ns: 'knowledge' })}
                        </button>
                      </>
                    ) : (
                      <span className={styles.mutedText}>{t('dynamicTools.categoryFormUploadOptional', { ns: 'knowledge' })}</span>
                    )}
                    {isEditingCategory && categoryFormUploadFile ? (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => void onUploadCategoryFormFile()}
                        disabled={categoryFormUploading || creatingCategory}
                      >
                        {categoryFormUploading
                          ? t(categoryDataSourceType === 'csv' ? 'dynamicTools.uploadingCategoryCsv' : 'dynamicTools.uploadingCategoryJson', { ns: 'knowledge' })
                          : t(categoryDataSourceType === 'csv' ? 'dynamicTools.uploadCategoryCsv' : 'dynamicTools.uploadCategoryJson', { ns: 'knowledge' })}
                      </button>
                    ) : null}
                  </div>
                  <label className={styles.field} style={{ marginTop: 12 }}>
                    <span className={styles.fieldLabel}>{t('dynamicTools.categoryFormUploadTitle', { ns: 'knowledge' })}</span>
                    <input
                      className={styles.textField}
                      value={categoryFormUploadTitle}
                      disabled={!categoryFormUploadFile || categoryFormUploading || creatingCategory}
                      onChange={(event) => setCategoryFormUploadTitle(event.target.value)}
                      placeholder={t('dynamicTools.categoryFormUploadTitlePlaceholder', { ns: 'knowledge' })}
                    />
                  </label>
                  <p className={styles.mutedText}>
                    {isEditingCategory
                      ? t('dynamicTools.categoryFormUploadHelpEdit', { ns: 'knowledge' })
                      : t('dynamicTools.categoryFormUploadHelpCreate', { ns: 'knowledge' })}
                  </p>
                </div>
              </>
            ) : categoryDataSourceType === 'google_sheets' ? (
              <>
                <label className={`${styles.field} ${styles.fieldWide}`}>
                  <span className={styles.fieldLabel}>{t('dynamicTools.googleSheetsSpreadsheet', { ns: 'knowledge' })}</span>
                  <input
                    className={styles.textField}
                    value={googleSheetsSpreadsheetId}
                    onChange={(event) => setGoogleSheetsSpreadsheetId(event.target.value)}
                    placeholder={t('dynamicTools.googleSheetsSpreadsheetPlaceholder', { ns: 'knowledge' })}
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('dynamicTools.googleSheetsSheetGid', { ns: 'knowledge' })}</span>
                  <input
                    className={styles.textField}
                    value={googleSheetsSheetGid}
                    onChange={(event) => setGoogleSheetsSheetGid(event.target.value)}
                    placeholder="0"
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('dynamicTools.googleSheetsAuthType', { ns: 'knowledge' })}</span>
                  <select
                    className={styles.textField}
                    value={googleSheetsAuthType}
                    onChange={(event) => setGoogleSheetsAuthType(event.target.value as 'public' | 'service_account' | 'oauth')}
                  >
                    <option value="public">{t('dynamicTools.googleSheetsAuthPublic', { ns: 'knowledge' })}</option>
                    <option value="service_account">{t('dynamicTools.googleSheetsAuthServiceAccount', { ns: 'knowledge' })}</option>
                    <option value="oauth">{t('dynamicTools.googleSheetsAuthOAuth', { ns: 'knowledge' })}</option>
                  </select>
                </label>
                {googleSheetsAuthType === 'service_account' ? (
                  <label className={`${styles.field} ${styles.fieldWide}`}>
                    <span className={styles.fieldLabel}>{t('dynamicTools.googleSheetsAuthValue', { ns: 'knowledge' })}</span>
                    <textarea
                      className={styles.textField}
                      rows={6}
                      value={googleSheetsAuthValue}
                      onChange={(event) => setGoogleSheetsAuthValue(event.target.value)}
                      placeholder={t('dynamicTools.googleSheetsAuthValuePlaceholder', { ns: 'knowledge' })}
                    />
                    {isEditingCategory && googleSheetsAuthConfigured ? (
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                        <input
                          type="checkbox"
                          checked={clearGoogleSheetsAuthValue}
                          onChange={(event) => setClearGoogleSheetsAuthValue(event.target.checked)}
                        />
                        <span>{t('dynamicTools.googleSheetsAuthClear', { ns: 'knowledge' })}</span>
                      </label>
                    ) : null}
                  </label>
                ) : null}
                {googleSheetsAuthType === 'oauth' ? (
                  <div className={`${styles.field} ${styles.fieldWide}`}>
                    <p className={styles.mutedText}>
                      {googleSheetsAuthConfigured || googleSheetsGoogleAccountConnected
                        ? t('dynamicTools.googleSheetsOAuthConfigured', { ns: 'knowledge' })
                        : t('dynamicTools.googleSheetsOAuthHelp', { ns: 'knowledge' })}
                    </p>
                    {googleSheetsAuthConfigured || googleSheetsGoogleAccountConnected ? null : (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={connectingGoogleSheetsTenantOAuth}
                      onClick={() => void onConnectGoogleSheetsTenantOAuth()}
                    >
                      {connectingGoogleSheetsTenantOAuth
                        ? t('dynamicTools.googleSheetsOAuthConnecting', { ns: 'knowledge' })
                        : t('dynamicTools.googleSheetsOAuthConnect', { ns: 'knowledge' })}
                    </button>
                    )}
                  </div>
                ) : null}
                <label className={`${styles.field} ${styles.fieldWide}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={googleSheetsSyncScheduleEnabled}
                    onChange={(event) => {
                      const enabled = event.target.checked;
                      setGoogleSheetsSyncScheduleEnabled(enabled);
                      if (enabled && googleSheetsSyncIntervalMinutes <= 0) {
                        setGoogleSheetsSyncIntervalMinutes(DEFAULT_CATEGORY_SYNC_INTERVAL_MINUTES);
                      }
                    }}
                  />
                  <span>{t('dynamicTools.enableBackgroundSync', { ns: 'knowledge', defaultValue: 'Enable scheduled background sync' })}</span>
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('dynamicTools.googleSheetsSyncIntervalMinutes', { ns: 'knowledge' })}</span>
                  <input
                    className={styles.textField}
                    type="number"
                    min={5}
                    max={10080}
                    value={googleSheetsSyncIntervalMinutes}
                    disabled={!googleSheetsSyncScheduleEnabled}
                    onChange={(event) => setGoogleSheetsSyncIntervalMinutes(Number(event.target.value) || 0)}
                  />
                </label>
                <p className={styles.mutedText}>{t('dynamicTools.googleSheetsSourceHelp', { ns: 'knowledge' })}</p>
                <p className={styles.mutedText}>{t('dynamicTools.googleSheetsSyncIntervalHelp', { ns: 'knowledge' })}</p>
              </>
            ) : (
              <>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('dynamicTools.consultationTemplate', { ns: 'knowledge' })}</span>
                  <select
                    className={styles.textField}
                    value={consultationTemplateId}
                    onChange={(event) => {
                      setConsultationTemplateId(event.target.value);
                      setConsultationFieldDefinitionId('');
                      setConsultationAdditionalFieldDefinitionIds([]);
                    }}
                  >
                    <option value="">{t('dynamicTools.selectConsultationTemplate', { ns: 'knowledge' })}</option>
                    {consultationTemplates.map((template) => (
                      <option key={template.id} value={template.id}>{template.name}</option>
                    ))}
                  </select>
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('dynamicTools.consultationField', { ns: 'knowledge' })}</span>
                  <select
                    className={styles.textField}
                    value={consultationFieldDefinitionId}
                    disabled={!consultationTemplateId}
                    onChange={(event) => {
                      setConsultationFieldDefinitionId(event.target.value);
                      setConsultationAdditionalFieldDefinitionIds((current) =>
                        current.filter((fieldId) => fieldId !== event.target.value));
                    }}
                  >
                    <option value="">{t('dynamicTools.selectConsultationField', { ns: 'knowledge' })}</option>
                    {consultationFieldOptions.map((field) => (
                      <option key={field.id} value={field.id}>{field.name} ({field.fieldType})</option>
                    ))}
                  </select>
                </label>
                <label className={`${styles.field} ${styles.fieldWide}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={consultationSyncScheduleEnabled}
                    onChange={(event) => {
                      const enabled = event.target.checked;
                      setConsultationSyncScheduleEnabled(enabled);
                      if (enabled && consultationSyncIntervalMinutes <= 0) {
                        setConsultationSyncIntervalMinutes(DEFAULT_CATEGORY_SYNC_INTERVAL_MINUTES);
                      }
                    }}
                  />
                  <span>{t('dynamicTools.enableBackgroundSync', { ns: 'knowledge', defaultValue: 'Enable scheduled background sync' })}</span>
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('dynamicTools.consultationSyncIntervalMinutes', { ns: 'knowledge' })}</span>
                  <input
                    className={styles.textField}
                    type="number"
                    min={5}
                    max={10080}
                    value={consultationSyncIntervalMinutes}
                    disabled={!consultationSyncScheduleEnabled}
                    onChange={(event) => setConsultationSyncIntervalMinutes(Number(event.target.value) || 0)}
                  />
                </label>
                <p className={styles.mutedText}>{t('dynamicTools.consultationSyncIntervalHelp', { ns: 'knowledge', defaultValue: 'When disabled, use Sync consultation data manually. Minimum 5 minutes when enabled.' })}</p>
                <div className={`${styles.field} ${styles.fieldWide}`}>
                  <span className={styles.fieldLabel}>{t('dynamicTools.consultationAdditionalFields', { ns: 'knowledge' })}</span>
                  {additionalConsultationFieldOptions.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px 16px' }}>
                      {additionalConsultationFieldOptions.map((field) => (
                        <label key={field.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <input
                            type="checkbox"
                            checked={consultationAdditionalFieldDefinitionIds.includes(field.id)}
                            disabled={!consultationFieldDefinitionId}
                            onChange={() => toggleConsultationAdditionalField(field.id)}
                          />
                          <span>{field.name} ({field.fieldType})</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className={styles.mutedText}>{t('dynamicTools.consultationAdditionalFieldsEmpty', { ns: 'knowledge' })}</p>
                  )}
                  <p className={styles.mutedText}>{t('dynamicTools.consultationAdditionalFieldsHelp', { ns: 'knowledge' })}</p>
                </div>
                <label className={`${styles.field} ${styles.fieldWide}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={consultationAutoSyncOnConsultationChange}
                    onChange={(event) => setConsultationAutoSyncOnConsultationChange(event.target.checked)}
                  />
                  <span>{t('dynamicTools.consultationAutoSyncOnChange', { ns: 'knowledge' })}</span>
                </label>
                <p className={styles.mutedText}>{t('dynamicTools.consultationAutoSyncOnChangeHelp', { ns: 'knowledge' })}</p>
                <label className={`${styles.field} ${styles.fieldWide}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={consultationAutoSyncOnFieldValueChange}
                    onChange={(event) => setConsultationAutoSyncOnFieldValueChange(event.target.checked)}
                  />
                  <span>{t('dynamicTools.consultationAutoSyncOnFieldValueChange', { ns: 'knowledge' })}</span>
                </label>
                <p className={styles.mutedText}>{t('dynamicTools.consultationAutoSyncOnFieldValueChangeHelp', { ns: 'knowledge' })}</p>
                <div className={`${styles.field} ${styles.fieldWide}`}>
                  <span className={styles.fieldLabel}>{t('dynamicTools.consultationIncludeStatuses', { ns: 'knowledge' })}</span>
                  {consultationStatusOptions.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px 16px' }}>
                      {consultationStatusOptions.map((status) => (
                        <label key={status.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <input
                            type="checkbox"
                            checked={consultationIncludeStatuses.includes(status.name)}
                            onChange={() => toggleConsultationStatus(status.name)}
                          />
                          <span>{status.name}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className={styles.mutedText}>{t('dynamicTools.consultationIncludeStatusesEmpty', { ns: 'knowledge' })}</p>
                  )}
                  <p className={styles.mutedText}>{t('dynamicTools.consultationIncludeStatusesHelp', { ns: 'knowledge' })}</p>
                </div>
                <div className={`${styles.field} ${styles.fieldWide}`}>
                  <span className={styles.fieldLabel}>{t('dynamicTools.consultationIncludeMetadata', { ns: 'knowledge' })}</span>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '8px 16px' }}>
                    {KNOWLEDGE_CONSULTATION_METADATA_FIELDS.map((field) => (
                      <label key={field} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <input
                          type="checkbox"
                          checked={consultationIncludeMetadataFields.includes(field)}
                          onChange={() => toggleConsultationMetadataField(field)}
                        />
                        <span>{t(`dynamicTools.consultationMetadata.${field}`, { ns: 'knowledge' })}</span>
                      </label>
                    ))}
                  </div>
                  <p className={styles.mutedText}>{t('dynamicTools.consultationIncludeMetadataHelp', { ns: 'knowledge' })}</p>
                </div>
                <label className={`${styles.field} ${styles.fieldWide}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={consultationFlattenRepeater}
                    onChange={(event) => setConsultationFlattenRepeater(event.target.checked)}
                  />
                  <span>{t('dynamicTools.consultationFlattenRepeater', { ns: 'knowledge' })}</span>
                </label>
                <p className={styles.mutedText}>{t('dynamicTools.consultationSourceHelp', { ns: 'knowledge' })}</p>
              </>
            )}
          </div>

          <div className={styles.dynamicToolsFormActions}>
            <button type="button" className="btn btn-secondary" onClick={() => goToBrowse('categories')}>
              {t('cancel', { ns: 'common', defaultValue: 'Cancel' })}
            </button>
            <button type="button" className="btn btn-primary" onClick={onCreateCategory} disabled={creatingCategory}>
              {creatingCategory
                ? t(isEditingCategory ? 'dynamicTools.updatingCategory' : 'dynamicTools.creatingCategory', { ns: 'knowledge' })
                : t(isEditingCategory ? 'dynamicTools.updateCategory' : 'dynamicTools.createCategory', { ns: 'knowledge' })}
            </button>
          </div>
        </section>
      </div>
    );
  }

  if (view === 'tool-form') {
    return (
      <div className={styles.dynamicToolsLayout}>
        <section className={`company-section ${styles.dynamicToolsEditorSection}`}>
          <div className={styles.dynamicToolsEditorHeader}>
            <div>
              <button type="button" className={styles.dynamicToolsBackLink} onClick={() => goToBrowse('tools')}>
                {t('dynamicTools.backToTools', { ns: 'knowledge' })}
              </button>
              <h3>
                {isEditingTool
                  ? t('dynamicTools.editToolTitle', { ns: 'knowledge' })
                  : t('dynamicTools.createToolTitle', { ns: 'knowledge' })}
              </h3>
              <p className={styles.pageIntro}>
                {isEditingTool
                  ? t('dynamicTools.editToolHelp', { ns: 'knowledge' })
                  : t('dynamicTools.toolsHelp', { ns: 'knowledge' })}
              </p>
            </div>
          </div>

          {!isEditingTool ? (
            <div className={styles.dynamicToolsFormGrid}>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>{t('dynamicTools.toolTemplate', { ns: 'knowledge' })}</span>
                <select
                  className={styles.selectField}
                  value={selectedTemplateId}
                  onChange={(event) => handleTemplateChange(event.target.value as ToolTemplateId)}
                >
                  <option value="">{t('dynamicTools.toolTemplateNone', { ns: 'knowledge' })}</option>
                  <option value="lookup">{t('dynamicTools.toolTemplateLookup', { ns: 'knowledge' })}</option>
                  <option value="search">{t('dynamicTools.toolTemplateSearch', { ns: 'knowledge' })}</option>
                  <option value="browse">{t('dynamicTools.toolTemplateBrowse', { ns: 'knowledge' })}</option>
                </select>
                <span className={styles.mutedText}>{t('dynamicTools.toolTemplateHelp', { ns: 'knowledge' })}</span>
              </label>
            </div>
          ) : null}

          <div className={styles.dynamicToolsFormGrid}>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('fileCategoryLabel', { ns: 'knowledge' })}</span>
              <select
                className={styles.selectField}
                value={selectedCategoryId}
                disabled={isEditingTool}
                onChange={(event) => {
                  setSelectedCategoryId(event.target.value);
                  const category = categories.find(item => item.categoryId === event.target.value);
                  setToolDataRootPath(category?.dataRootPath ?? '');
                  if (!isEditingTool) {
                    setSchemaFields([]);
                    setResponseFields([]);
                  }
                }}
              >
                <option value="">{t('fileCategoryPlaceholder', { ns: 'knowledge' })}</option>
                {categories.map(category => (
                  <option key={category.categoryId} value={category.categoryId}>
                    {category.displayName}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('dynamicTools.dataRootPath', { ns: 'knowledge' })}</span>
              <input
                className={styles.textField}
                value={toolDataRootPath}
                onChange={(event) => setToolDataRootPath(event.target.value)}
                placeholder={t('dynamicTools.dataRootPathPlaceholder', { ns: 'knowledge' })}
              />
            </label>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>{t('dynamicTools.introspectSchema', { ns: 'knowledge' })}</span>
              <button type="button" className="btn btn-secondary" onClick={onIntrospectSchema} disabled={introspecting || !selectedCategoryId}>
                {introspecting ? t('dynamicTools.introspecting', { ns: 'knowledge' }) : t('dynamicTools.loadSchema', { ns: 'knowledge' })}
              </button>
            </div>
          </div>

          <div className={styles.dynamicToolsFormGrid}>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('dynamicTools.kernelFunctionName', { ns: 'knowledge' })}</span>
              <input
                className={styles.textField}
                value={kernelFunctionName}
                onChange={(event) => setKernelFunctionName(event.target.value)}
                placeholder="SearchExamDates"
              />
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('dynamicTools.toolDisplayName', { ns: 'knowledge' })}</span>
              <input
                className={styles.textField}
                value={toolDisplayName}
                onChange={(event) => setToolDisplayName(event.target.value)}
              />
            </label>
            <label className={`${styles.field} ${styles.fieldWide}`}>
              <span className={styles.fieldLabel}>{t('dynamicTools.toolDescription', { ns: 'knowledge' })}</span>
              <input
                className={styles.textField}
                value={toolDescription}
                onChange={(event) => setToolDescription(event.target.value)}
              />
            </label>
          </div>

          <div className={styles.dynamicToolsParameterHeader}>
            <div>
              <h4>{t('dynamicTools.executionTitle', { ns: 'knowledge' })}</h4>
              <p className={styles.mutedText}>{t('dynamicTools.executionHelp', { ns: 'knowledge' })}</p>
            </div>
          </div>

          <div className={styles.dynamicToolsFormGrid}>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('dynamicTools.maxResults', { ns: 'knowledge' })}</span>
              <input
                className={styles.textField}
                type="number"
                min={1}
                max={500}
                value={maxResults}
                onChange={(event) => setMaxResults(Number(event.target.value) || 100)}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('dynamicTools.sortJsonPath', { ns: 'knowledge' })}</span>
              <input
                className={styles.textField}
                value={sortJsonPath}
                onChange={(event) => setSortJsonPath(event.target.value)}
                placeholder={t('dynamicTools.sortJsonPathPlaceholder', { ns: 'knowledge' })}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('dynamicTools.sortDirection', { ns: 'knowledge' })}</span>
              <select
                className={styles.selectField}
                value={sortDirection}
                onChange={(event) => setSortDirection(event.target.value === 'desc' ? 'desc' : 'asc')}
              >
                <option value="asc">{t('dynamicTools.sortAsc', { ns: 'knowledge' })}</option>
                <option value="desc">{t('dynamicTools.sortDesc', { ns: 'knowledge' })}</option>
              </select>
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('dynamicTools.filterLogic', { ns: 'knowledge' })}</span>
              <select
                className={styles.selectField}
                value={filterLogic}
                onChange={(event) => setFilterLogic(event.target.value === 'or' ? 'or' : 'and')}
              >
                <option value="and">{t('dynamicTools.filterLogicAnd', { ns: 'knowledge' })}</option>
                <option value="or">{t('dynamicTools.filterLogicOr', { ns: 'knowledge' })}</option>
              </select>
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('dynamicTools.resultMode', { ns: 'knowledge' })}</span>
              <select
                className={styles.selectField}
                value={resultMode}
                onChange={(event) => {
                  const nextMode = event.target.value;
                  setResultMode(nextMode === 'count' || nextMode === 'groupby' ? nextMode : 'list');
                }}
              >
                <option value="list">{t('dynamicTools.resultModeList', { ns: 'knowledge' })}</option>
                <option value="count">{t('dynamicTools.resultModeCount', { ns: 'knowledge' })}</option>
                <option value="groupby">{t('dynamicTools.resultModeGroupBy', { ns: 'knowledge' })}</option>
              </select>
            </label>
            {resultMode === 'groupby' ? (
              <label className={styles.field}>
                <span className={styles.fieldLabel}>{t('dynamicTools.groupByJsonPath', { ns: 'knowledge' })}</span>
                <input
                  className={styles.textField}
                  value={groupByJsonPath}
                  onChange={(event) => setGroupByJsonPath(event.target.value)}
                  placeholder={t('dynamicTools.groupByJsonPathPlaceholder', { ns: 'knowledge' })}
                />
              </label>
            ) : null}
          </div>

          <div className={styles.dynamicToolsParameterHeader}>
            <div>
              <h4>{t('dynamicTools.hybridKnowledgeTitle', { ns: 'knowledge' })}</h4>
              <p className={styles.mutedText}>{t('dynamicTools.hybridKnowledgeHelp', { ns: 'knowledge' })}</p>
            </div>
          </div>

          <div className={styles.dynamicToolsFormGrid}>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('dynamicTools.knowledgeSearchParameter', { ns: 'knowledge' })}</span>
              <select
                className={styles.selectField}
                value={knowledgeSearchParameter}
                onChange={(event) => setKnowledgeSearchParameter(event.target.value)}
              >
                <option value="">{t('dynamicTools.knowledgeSearchParameterNone', { ns: 'knowledge' })}</option>
                {schemaFields.filter(item => item.selected && item.name.trim()).map(parameter => (
                  <option key={parameter.draftId} value={parameter.name}>{parameter.name}</option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('dynamicTools.knowledgeSearchMaxChunks', { ns: 'knowledge' })}</span>
              <input
                className={styles.textField}
                type="number"
                min={1}
                max={10}
                value={knowledgeSearchMaxChunks}
                disabled={!knowledgeSearchParameter.trim()}
                onChange={(event) => setKnowledgeSearchMaxChunks(Number(event.target.value) || 3)}
              />
            </label>
          </div>

          <div className={styles.dynamicToolsParameterHeader}>
            <h4>{t('dynamicTools.parametersTitle', { ns: 'knowledge' })}</h4>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setSchemaFields(current => [...current, createEmptyParameter()])}
            >
              {t('dynamicTools.addParameter', { ns: 'knowledge' })}
            </button>
          </div>

          {schemaFields.length > 0 ? (
            <div className={styles.dynamicToolsParameterTableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('dynamicTools.useParameter', { ns: 'knowledge' })}</th>
                    <th>{t('dynamicTools.parameterName', { ns: 'knowledge' })}</th>
                    <th>{t('dynamicTools.parameterDescription', { ns: 'knowledge' })}</th>
                    <th>{t('dynamicTools.jsonPath', { ns: 'knowledge' })}</th>
                    <th>{t('dynamicTools.matchMode', { ns: 'knowledge' })}</th>
                    <th>{t('dynamicTools.parameterExample', { ns: 'knowledge' })}</th>
                    <th>{t('dynamicTools.parameterFormat', { ns: 'knowledge' })}</th>
                    <th>{t('dynamicTools.parameterDefault', { ns: 'knowledge' })}</th>
                    <th>{t('dynamicTools.allowedValues', { ns: 'knowledge' })}</th>
                    <th>{t('dynamicTools.required', { ns: 'knowledge' })}</th>
                    <th>{t('actions', { ns: 'knowledge', defaultValue: 'Actions' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {schemaFields.map((parameter, index) => (
                    <tr key={parameter.draftId}>
                      <td>
                        <input
                          type="checkbox"
                          checked={parameter.selected}
                          onChange={(event) => updateParameter(index, { selected: event.target.checked })}
                        />
                      </td>
                      <td>
                        <input
                          className={styles.textField}
                          value={parameter.name}
                          onChange={(event) => updateParameter(index, { name: event.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className={styles.textField}
                          value={parameter.description}
                          onChange={(event) => updateParameter(index, { description: event.target.value })}
                          placeholder={t('dynamicTools.parameterDescriptionPlaceholder', { ns: 'knowledge' })}
                        />
                      </td>
                      <td>
                        <input
                          className={styles.textField}
                          value={parameter.jsonPath}
                          onChange={(event) => updateParameter(index, { jsonPath: event.target.value })}
                        />
                      </td>
                      <td>
                        <select
                          className={styles.selectField}
                          value={parameter.matchMode}
                          onChange={(event) => updateParameter(index, { matchMode: event.target.value })}
                        >
                          {MATCH_MODES.map(mode => (
                            <option key={mode} value={mode}>{mode}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          className={styles.textField}
                          value={parameter.example ?? ''}
                          onChange={(event) => updateParameter(index, { example: event.target.value })}
                          placeholder={t('dynamicTools.parameterExamplePlaceholder', { ns: 'knowledge' })}
                        />
                      </td>
                      <td>
                        <select
                          className={styles.selectField}
                          value={parameter.format ?? ''}
                          onChange={(event) => updateParameter(index, { format: event.target.value })}
                        >
                          {PARAMETER_FORMATS.map(format => (
                            <option key={format || 'none'} value={format}>
                              {format || t('dynamicTools.formatNone', { ns: 'knowledge' })}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          className={styles.textField}
                          value={parameter.defaultValue ?? ''}
                          onChange={(event) => updateParameter(index, { defaultValue: event.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className={styles.textField}
                          value={parameter.allowedValuesText ?? formatAllowedValues(parameter.allowedValues)}
                          onChange={(event) => updateParameter(index, {
                            allowedValuesText: event.target.value,
                          })}
                          onBlur={(event) => updateParameter(index, {
                            allowedValuesText: event.target.value,
                            allowedValues: parseAllowedValues(event.target.value),
                          })}
                          placeholder={t('dynamicTools.allowedValuesPlaceholder', { ns: 'knowledge' })}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={parameter.isRequired}
                          onChange={(event) => updateParameter(index, { isRequired: event.target.checked })}
                        />
                      </td>
                      <td>
                        <button type="button" className="btn btn-secondary" onClick={() => removeParameter(index)}>
                          {t('delete', { ns: 'common' })}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className={styles.emptyState}>{t('dynamicTools.noParametersYet', { ns: 'knowledge' })}</p>
          )}

          <div className={styles.dynamicToolsParameterHeader}>
            <div>
              <h4>{t('dynamicTools.responseFieldsTitle', { ns: 'knowledge' })}</h4>
              <p className={styles.mutedText}>{t('dynamicTools.responseFieldsHelp', { ns: 'knowledge' })}</p>
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setResponseFields(current => [...current, createEmptyResponseField()])}
            >
              {t('dynamicTools.addResponseField', { ns: 'knowledge' })}
            </button>
          </div>

          {responseFields.length > 0 ? (
            <div className={styles.dynamicToolsParameterTableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('dynamicTools.useParameter', { ns: 'knowledge' })}</th>
                    <th>{t('dynamicTools.responseFieldName', { ns: 'knowledge' })}</th>
                    <th>{t('dynamicTools.jsonPath', { ns: 'knowledge' })}</th>
                    <th>{t('actions', { ns: 'knowledge', defaultValue: 'Actions' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {responseFields.map((field, index) => (
                    <tr key={field.draftId}>
                      <td>
                        <input
                          type="checkbox"
                          checked={field.selected}
                          onChange={(event) => updateResponseField(index, { selected: event.target.checked })}
                        />
                      </td>
                      <td>
                        <input
                          className={styles.textField}
                          value={field.name}
                          onChange={(event) => updateResponseField(index, { name: event.target.value })}
                          placeholder={t('dynamicTools.responseFieldNamePlaceholder', { ns: 'knowledge' })}
                        />
                      </td>
                      <td>
                        <input
                          className={styles.textField}
                          value={field.jsonPath}
                          onChange={(event) => updateResponseField(index, { jsonPath: event.target.value })}
                        />
                      </td>
                      <td>
                        <button type="button" className="btn btn-secondary" onClick={() => removeResponseField(index)}>
                          {t('delete', { ns: 'common' })}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className={styles.emptyState}>{t('dynamicTools.noResponseFieldsYet', { ns: 'knowledge' })}</p>
          )}

          <label className={`${styles.field} ${styles.fieldWide}`} style={{ marginTop: 16 }}>
            <span className={styles.fieldLabel}>{t('dynamicTools.responseInstruction', { ns: 'knowledge' })}</span>
            <textarea
              className={styles.textArea}
              rows={4}
              value={responseInstruction}
              onChange={(event) => setResponseInstruction(event.target.value)}
              placeholder={t('dynamicTools.responseInstructionPlaceholder', { ns: 'knowledge' })}
            />
            <span className={styles.mutedText}>{t('dynamicTools.responseInstructionHelp', { ns: 'knowledge' })}</span>
          </label>

          <div className={styles.dynamicToolsPreviewSection}>
            <div className={styles.dynamicToolsParameterHeader}>
              <div>
                <h4>{t('dynamicTools.previewTitle', { ns: 'knowledge' })}</h4>
                <p className={styles.mutedText}>{t('dynamicTools.previewHelp', { ns: 'knowledge' })}</p>
              </div>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => void onPreviewTool()}
                disabled={previewLoading || !selectedCategoryId}
              >
                {previewLoading ? t('dynamicTools.previewRunning', { ns: 'knowledge' }) : t('dynamicTools.runPreview', { ns: 'knowledge' })}
              </button>
            </div>

            {selectedPreviewParameters.length > 0 ? (
              <div className={styles.dynamicToolsPreviewInputs}>
                {selectedPreviewParameters.map(parameter => (
                  <label key={parameter.draftId} className={styles.field}>
                    <span className={styles.fieldLabel}>
                      {parameter.name || parameter.jsonPath}
                      {parameter.isRequired
                        ? ` (${t('dynamicTools.previewInputRequired', { ns: 'knowledge' })})`
                        : ` (${t('dynamicTools.previewInputOptional', { ns: 'knowledge' })})`}
                    </span>
                    {isUnaryMatchMode(parameter.matchMode) ? (
                      <p className={styles.mutedText}>
                        {t('dynamicTools.previewInputUnaryHint', {
                          ns: 'knowledge',
                          mode: parameter.matchMode,
                        })}
                      </p>
                    ) : (
                      <>
                        <input
                          className={styles.textField}
                          value={previewArguments[parameter.name] ?? ''}
                          onChange={(event) => setPreviewArguments(current => ({
                            ...current,
                            [parameter.name]: event.target.value,
                          }))}
                          placeholder={parameter.example?.trim() || t('dynamicTools.previewInputPlaceholder', { ns: 'knowledge' })}
                        />
                        {parameter.defaultValue?.trim() ? (
                          <span className={styles.mutedText}>
                            {t('dynamicTools.previewInputDefaultHint', {
                              ns: 'knowledge',
                              value: parameter.defaultValue.trim(),
                            })}
                          </span>
                        ) : null}
                      </>
                    )}
                  </label>
                ))}
              </div>
            ) : (
              <p className={styles.emptyState}>{t('dynamicTools.previewNeedsParameters', { ns: 'knowledge' })}</p>
            )}

            {previewResult ? (
              <div className={styles.dynamicToolsPreviewResult}>
                {previewResult.error ? (
                  <p className={styles.dynamicToolsPreviewError}>{previewResult.error}</p>
                ) : (
                  <p className={styles.mutedText}>
                    {buildDynamicToolPreviewSummary(previewResult, t)}
                  </p>
                )}
                {getDynamicToolPreviewGroups(previewResult).length > 0 ? (
                  <div className={styles.dynamicToolsPreviewGroups}>
                    {getDynamicToolPreviewGroups(previewResult).slice(0, 8).map((group, index) => (
                      <div key={`${String(group.value ?? '')}-${index}`} className={styles.dynamicToolsPreviewGroupRow}>
                        <span>{String(group.value ?? '')}</span>
                        <span>{Number(group.count ?? 0)}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
                <pre className={styles.dynamicToolsPreviewJson}>
                  {JSON.stringify(previewResult, null, 2)}
                </pre>
              </div>
            ) : null}
          </div>

          <label className={styles.dynamicToolsCheckboxRow}>
            <input
              type="checkbox"
              checked={isPublished}
              onChange={(event) => setIsPublished(event.target.checked)}
            />
            <span>
              <strong>{t('dynamicTools.isPublishedTitle', { ns: 'knowledge' })}</strong>
              <span className={styles.mutedText}>{t('dynamicTools.isPublishedHelp', { ns: 'knowledge' })}</span>
            </span>
          </label>

          <ToolUseCaseAssignment
            selectedUseCaseIds={assignedUseCaseIds}
            disabled={savingTool}
            isPublished={isPublished}
            onChange={setAssignedUseCaseIds}
          />

          <div className={styles.dynamicToolsFormActions}>
            <button type="button" className="btn btn-secondary" onClick={() => goToBrowse('tools')}>
              {t('cancel', { ns: 'common', defaultValue: 'Cancel' })}
            </button>
            <button type="button" className="btn btn-primary" onClick={onSaveTool} disabled={savingTool || !selectedCategoryId}>
              {savingTool
                ? t(isEditingTool ? 'dynamicTools.updatingTool' : 'dynamicTools.creatingTool', { ns: 'knowledge' })
                : t(isEditingTool ? 'dynamicTools.updateTool' : 'dynamicTools.createTool', { ns: 'knowledge' })}
            </button>
          </div>
        </section>
      </div>
    );
  }

  const isBrowseCategories = view === 'browse-categories';

  return (
    <div className={styles.dynamicToolsLayout}>
      <section className="company-section">
        {showPageHeader ? (
          <h3>{t('dynamicTools.tabTitle', { ns: 'knowledge' })}</h3>
        ) : null}
        <p className={styles.pageIntro}>
          {isBrowseCategories
            ? t('dynamicTools.browseCategoriesIntro', { ns: 'knowledge' })
            : t('dynamicTools.browseToolsIntro', { ns: 'knowledge' })}
        </p>
        <div className={styles.tabBar}>
          <button
            type="button"
            className={`${styles.tab} ${isBrowseCategories ? styles.tabActive : ''}`}
            onClick={() => setView('browse-categories')}
          >
            {t('dynamicTools.browseTabCategories', {
              ns: 'knowledge',
              count: categories.length,
            })}
          </button>
          <button
            type="button"
            className={`${styles.tab} ${!isBrowseCategories ? styles.tabActive : ''}`}
            onClick={() => setView('browse-tools')}
          >
            {t('dynamicTools.browseTabTools', {
              ns: 'knowledge',
              count: tools.length,
            })}
          </button>
        </div>
      </section>

      {isBrowseCategories ? (
      <>
      {canManageKnowledge ? <section
        className={`company-section ${styles.googleSheetsOAuthCard}`}
        aria-busy={googleSheetsTenantOAuthBusy}
      >
        <div className={styles.dynamicToolsSectionHeader}>
          <div>
            <div className={styles.googleSheetsOAuthTitleRow}>
              <h3>{t('dynamicTools.googleSheetsTenantSettingsTitle', { ns: 'knowledge' })}</h3>
              {googleSheetsTenantSettingsHydrated ? (
                <span
                  className={`${styles.googleSheetsOAuthBadge} ${
                    googleSheetsTenantOAuthStatus === 'ready'
                      ? styles.googleSheetsOAuthBadgeReady
                      : googleSheetsTenantOAuthStatus === 'needs-setup'
                        ? styles.googleSheetsOAuthBadgeMuted
                        : styles.googleSheetsOAuthBadgeWarn
                  }`}
                >
                  {googleSheetsTenantOAuthStatus === 'ready'
                    ? t('dynamicTools.googleSheetsTenantOAuthStatusReady', { ns: 'knowledge' })
                    : googleSheetsTenantOAuthStatus === 'needs-connect'
                      ? t('dynamicTools.googleSheetsTenantOAuthStatusNeedsConnect', { ns: 'knowledge' })
                      : googleSheetsTenantOAuthStatus === 'check-id'
                        ? t('dynamicTools.googleSheetsTenantOAuthStatusCheckClientId', { ns: 'knowledge' })
                        : t('dynamicTools.googleSheetsTenantOAuthStatusNeedsSetup', { ns: 'knowledge' })}
                </span>
              ) : null}
            </div>
            <p className={styles.mutedText}>{t('dynamicTools.googleSheetsTenantSettingsHelp', { ns: 'knowledge' })}</p>
          </div>
        </div>
        <div className={styles.googleSheetsOAuthBody}>
            {connectingGoogleSheetsTenantOAuth || disconnectingGoogleSheetsAccount || savingGoogleSheetsTenantSettings ? (
              <>
                <span className={styles.busyBar} aria-hidden="true" />
                <div className={styles.busyOverlay} role="status" aria-live="polite">
                  <Spinner size={36} />
                  <div className={styles.busyCopy}>
                    <strong>
                      {connectingGoogleSheetsTenantOAuth
                        ? t('dynamicTools.googleSheetsOAuthConnecting', { ns: 'knowledge' })
                        : disconnectingGoogleSheetsAccount
                          ? t('dynamicTools.googleSheetsOAuthDisconnecting', { ns: 'knowledge' })
                          : t('dynamicTools.googleSheetsTenantSettingsSaving', { ns: 'knowledge' })}
                    </strong>
                    <span>{t('dynamicTools.googleSheetsTenantSettingsSavingHelp', { ns: 'knowledge' })}</span>
                  </div>
                </div>
              </>
            ) : null}
            {loadingGoogleSheetsTenantSettings ? (
              <div className={styles.loadingRow}>
                <Spinner size={16} />
                <span>{t('dynamicTools.googleSheetsTenantSettingsLoading', { ns: 'knowledge' })}</span>
              </div>
            ) : (
              <>
                <p className={styles.mutedText}>
                  {googleSheetsGoogleAccountConnected
                    ? t('dynamicTools.googleSheetsTenantAccountConnectedHelp', { ns: 'knowledge' })
                    : googleSheetsOAuthAppConfigured
                      ? t('dynamicTools.googleSheetsTenantConnectHelp', { ns: 'knowledge' })
                      : t('dynamicTools.googleSheetsOAuthAppMissing', { ns: 'knowledge' })}
                </p>
                {googleSheetsTenantOAuthFrontendReturnUrlResolved ? (
                  <p className={styles.fieldHint}>
                    {t('dynamicTools.googleSheetsTenantReturnOnThisDomain', {
                      ns: 'knowledge',
                      url: googleSheetsTenantOAuthFrontendReturnUrlResolved,
                    })}
                  </p>
                ) : null}
                <div className={styles.googleSheetsOAuthActions}>
                  <button
                    type="button"
                    className={`btn btn-primary ${styles.googleSheetsOAuthSave}`}
                    disabled={connectingGoogleSheetsTenantOAuth || disconnectingGoogleSheetsAccount}
                    onClick={() => void onConnectGoogleSheetsTenantOAuth()}
                  >
                    {connectingGoogleSheetsTenantOAuth
                      ? <span className={styles.btnSpinner} aria-hidden="true" />
                      : null}
                    <span>
                      {connectingGoogleSheetsTenantOAuth
                        ? t('dynamicTools.googleSheetsOAuthConnecting', { ns: 'knowledge' })
                        : googleSheetsGoogleAccountConnected
                          ? t('dynamicTools.googleSheetsOAuthReconnect', { ns: 'knowledge' })
                          : t('dynamicTools.googleSheetsOAuthConnect', { ns: 'knowledge' })}
                    </span>
                  </button>
                  {googleSheetsGoogleAccountConnected ? (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={disconnectingGoogleSheetsAccount || connectingGoogleSheetsTenantOAuth}
                      onClick={() => void onDisconnectGoogleSheetsAccount()}
                    >
                      {t('dynamicTools.googleSheetsOAuthDisconnect', { ns: 'knowledge' })}
                    </button>
                  ) : null}
                </div>

                {googleSheetsOAuthAppManagedByPlatform ? null : (
                  <>
                    <button
                      type="button"
                      className={styles.googleSheetsAdvancedToggle}
                      disabled={savingGoogleSheetsTenantSettings}
                      onClick={() => setGoogleSheetsTenantSettingsOpen((current) => !current)}
                    >
                      {googleSheetsTenantSettingsOpen
                        ? t('dynamicTools.googleSheetsTenantOAuthAdvancedHide', { ns: 'knowledge' })
                        : t('dynamicTools.googleSheetsTenantUseOwnApp', { ns: 'knowledge' })}
                    </button>

                    {googleSheetsTenantSettingsOpen ? (
                      <>
                        <ol className={styles.googleSheetsOAuthSteps}>
                          <li>
                            {t('dynamicTools.googleSheetsTenantOAuthStep1', { ns: 'knowledge' })}{' '}
                            <a href={GOOGLE_CLOUD_CREDENTIALS_URL} target="_blank" rel="noreferrer">
                              {t('dynamicTools.googleSheetsTenantOAuthStep1Link', { ns: 'knowledge' })}
                            </a>
                          </li>
                          <li>{t('dynamicTools.googleSheetsTenantOAuthStep2', { ns: 'knowledge' })}</li>
                          <li>{t('dynamicTools.googleSheetsTenantOAuthStep3', { ns: 'knowledge' })}</li>
                        </ol>

                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>
                            {t('dynamicTools.googleSheetsTenantOAuthRedirectUri', { ns: 'knowledge' })}
                          </span>
                          <div className={styles.googleSheetsCopyRow}>
                            <input
                              className={`${styles.textField} ${styles.googleSheetsCopyValue}`}
                              value={googleSheetsTenantOAuthRedirectUri}
                              readOnly
                              spellCheck={false}
                            />
                            <button
                              type="button"
                              className="btn btn-secondary"
                              disabled={!googleSheetsTenantOAuthRedirectUri.trim() || savingGoogleSheetsTenantSettings}
                              onClick={() => void copyGoogleSheetsOAuthValue(googleSheetsTenantOAuthRedirectUri, 'redirect')}
                            >
                              {copiedGoogleSheetsField === 'redirect'
                                ? t('dynamicTools.googleSheetsTenantOAuthCopied', { ns: 'knowledge' })
                                : t('copy', { ns: 'common' })}
                            </button>
                          </div>
                          <span className={styles.fieldHint}>
                            {t('dynamicTools.googleSheetsTenantOAuthRedirectUriHint', { ns: 'knowledge' })}
                          </span>
                        </div>

                        <label className={styles.field}>
                          <span className={styles.fieldLabel}>
                            {t('dynamicTools.googleSheetsTenantOAuthClientId', { ns: 'knowledge' })}
                          </span>
                          <input
                            className={styles.textField}
                            value={googleSheetsTenantOAuthClientId}
                            onChange={(event) => setGoogleSheetsTenantOAuthClientId(event.target.value)}
                            placeholder={t('dynamicTools.googleSheetsTenantOAuthClientIdPlaceholder', { ns: 'knowledge' })}
                            autoComplete="off"
                            spellCheck={false}
                            name="google-oauth-client-id"
                            disabled={savingGoogleSheetsTenantSettings}
                          />
                          <span className={styles.fieldHint}>
                            {t('dynamicTools.googleSheetsTenantOAuthClientIdHint', { ns: 'knowledge' })}
                          </span>
                          {googleSheetsTenantClientIdLooksLikeEmail ? (
                            <span className={styles.fieldWarning}>
                              {t('dynamicTools.googleSheetsTenantOAuthClientIdEmailWarning', { ns: 'knowledge' })}
                            </span>
                          ) : null}
                        </label>

                        <div className={styles.field}>
                          <span className={styles.fieldLabel}>
                            {t('dynamicTools.googleSheetsTenantOAuthClientSecret', { ns: 'knowledge' })}
                          </span>
                          <div className={styles.googleSheetsCopyRow}>
                            <input
                              className={styles.textField}
                              type={googleSheetsTenantOAuthSecretVisible ? 'text' : 'password'}
                              value={googleSheetsTenantOAuthClientSecret}
                              onChange={(event) => {
                                setGoogleSheetsTenantOAuthClientSecret(event.target.value);
                                if (event.target.value) {
                                  setClearGoogleSheetsTenantOAuthSecret(false);
                                }
                              }}
                              placeholder={googleSheetsTenantOAuthSecretConfigured
                                ? t('dynamicTools.googleSheetsTenantOAuthClientSecretPlaceholder', { ns: 'knowledge' })
                                : t('dynamicTools.googleSheetsTenantOAuthClientSecretPlaceholderEmpty', { ns: 'knowledge' })}
                              autoComplete="new-password"
                              name="google-oauth-client-secret"
                              disabled={savingGoogleSheetsTenantSettings || clearGoogleSheetsTenantOAuthSecret}
                            />
                            <button
                              type="button"
                              className="btn btn-secondary"
                              disabled={savingGoogleSheetsTenantSettings}
                              onClick={() => setGoogleSheetsTenantOAuthSecretVisible((current) => !current)}
                            >
                              {googleSheetsTenantOAuthSecretVisible
                                ? t('dynamicTools.googleSheetsTenantOAuthHideSecret', { ns: 'knowledge' })
                                : t('dynamicTools.googleSheetsTenantOAuthShowSecret', { ns: 'knowledge' })}
                            </button>
                          </div>
                        </div>

                        <div className={styles.googleSheetsOAuthActions}>
                          <button
                            type="button"
                            className={`btn btn-primary ${styles.googleSheetsOAuthSave}`}
                            disabled={savingGoogleSheetsTenantSettings}
                            onClick={() => void onSaveGoogleSheetsTenantSettings()}
                          >
                            {savingGoogleSheetsTenantSettings
                              ? <span className={styles.btnSpinner} aria-hidden="true" />
                              : null}
                            <span>
                              {savingGoogleSheetsTenantSettings
                                ? t('dynamicTools.googleSheetsTenantSettingsSaving', { ns: 'knowledge' })
                                : t('dynamicTools.googleSheetsTenantSettingsSave', { ns: 'knowledge' })}
                            </span>
                          </button>
                        </div>
                      </>
                    ) : null}
                  </>
                )}
              </>
            )}
          </div>

      </section> : null}

      <section className="company-section">
        <div className={styles.dynamicToolsSectionHeader}>
          <div>
            <h3>{t('dynamicTools.categoriesTitle', { ns: 'knowledge' })}</h3>
            <p className={styles.mutedText}>{t('dynamicTools.categoriesBrowseHelp', { ns: 'knowledge' })}</p>
          </div>
          {canManageKnowledge ? (
            <button type="button" className="btn btn-secondary" onClick={openCreateCategory}>
              {t('dynamicTools.createCategory', { ns: 'knowledge' })}
            </button>
          ) : null}
        </div>

        <input
          ref={categoryUploadInputRef}
          type="file"
          accept={
            categories.find((item) => item.categoryId === pendingUploadCategoryId)?.dataSourceType === 'csv'
              ? '.csv,text/csv'
              : '.json,application/json'
          }
          style={{ display: 'none' }}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file && pendingUploadCategoryId) {
              void onUploadCategoryFile(pendingUploadCategoryId, file);
            }
          }}
        />

        {categories.length > 0 ? (
          <div className={styles.dynamicToolsBrowseList}>
            {categories.map(category => (
              <div key={category.categoryId} className={styles.dynamicToolsBrowseRow}>
                <div className={styles.dynamicToolsBrowseMain}>
                  <strong>{category.displayName}</strong>
                  <div className={styles.mutedText}>
                    {t(
                      category.dataSourceType === 'consultation'
                        ? 'dynamicTools.dataSourceConsultation'
                        : category.dataSourceType === 'csv'
                          ? 'dynamicTools.dataSourceCsv'
                          : category.dataSourceType === 'google_sheets'
                            ? 'dynamicTools.dataSourceGoogleSheets'
                            : 'dynamicTools.dataSourceDocument',
                      { ns: 'knowledge' },
                    )}
                    {' · '}
                    {category.categoryId}
                  </div>
                  {category.dataRootPath ? (
                    <div className={styles.mutedText}>
                      {t('dynamicTools.dataRootPath', { ns: 'knowledge' })}: {category.dataRootPath}
                    </div>
                  ) : null}
                  <div className={styles.mutedText}>
                    {t('dynamicTools.categoryStats', {
                      ns: 'knowledge',
                      tools: category.toolCount,
                      documents: category.documentCount,
                      ready: category.readyDocumentCount,
                    })}
                  </div>
                  {category.latestDocumentTitle ? (
                    <div className={styles.mutedText}>
                      {t('dynamicTools.latestDocumentLabel', {
                        ns: 'knowledge',
                        title: category.latestDocumentTitle,
                        date: category.latestDocumentUpdatedAt
                          ? new Date(category.latestDocumentUpdatedAt).toLocaleString()
                          : '',
                      })}
                    </div>
                  ) : category.dataSourceType === 'consultation' ? (
                    <div className={styles.mutedText}>{t('dynamicTools.noDocumentsConsultationHint', { ns: 'knowledge' })}</div>
                  ) : category.dataSourceType === 'csv' ? (
                    <div className={styles.mutedText}>{t('dynamicTools.noDocumentsCsvHint', { ns: 'knowledge' })}</div>
                  ) : category.dataSourceType === 'google_sheets' ? (
                    <div className={styles.mutedText}>{t('dynamicTools.noDocumentsGoogleSheetsHint', { ns: 'knowledge' })}</div>
                  ) : (
                    <div className={styles.mutedText}>{t('dynamicTools.noDocumentsYet', { ns: 'knowledge' })}</div>
                  )}
                  {category.externalSyncUrl ? (
                    <div className={styles.mutedText}>
                      {t('dynamicTools.externalSyncConfigured', { ns: 'knowledge' })}: {category.externalSyncUrl}
                    </div>
                  ) : null}
                  {category.dataSourceType === 'google_sheets' && category.googleSheetsSource?.spreadsheetId ? (
                    <div className={styles.mutedText}>
                      {t('dynamicTools.googleSheetsSourceBadge', {
                        ns: 'knowledge',
                        spreadsheetId: category.googleSheetsSource.spreadsheetId,
                        sheetGid: category.googleSheetsSource.sheetGid || '0',
                      })}
                      {' · '}
                      {t(`dynamicTools.googleSheetsAuthBadge.${category.googleSheetsSource.authType}`, {
                        ns: 'knowledge',
                        defaultValue: category.googleSheetsSource.authType,
                      })}
                      {category.googleSheetsSource.authConfigured
                        ? ` (${t('dynamicTools.googleSheetsAuthConfiguredShort', { ns: 'knowledge' })})`
                        : ''}
                    </div>
                  ) : null}
                  {category.dataSourceType === 'consultation' ? (
                    <div className={styles.mutedText}>
                      {t('dynamicTools.consultationSourceBadge', {
                        ns: 'knowledge',
                        template: resolveConsultationTemplateName(category.consultationSource?.templateId),
                        field: resolveConsultationFieldName(category.consultationSource?.fieldDefinitionId),
                      })}
                    </div>
                  ) : null}
                  {category.externalSyncUrl && category.externalSyncIntervalMinutes > 0 ? (
                    <div className={styles.mutedText}>
                      {t('dynamicTools.externalSyncScheduleLabel', {
                        ns: 'knowledge',
                        minutes: category.externalSyncIntervalMinutes,
                      })}
                    </div>
                  ) : null}
                  {category.externalSyncUrl && category.externalSyncIntervalMinutes <= 0 ? (
                    <div className={styles.mutedText}>
                      {t('dynamicTools.backgroundSyncDisabledLabel', { ns: 'knowledge', defaultValue: 'Background sync disabled (manual sync only)' })}
                    </div>
                  ) : null}
                  {category.dataSourceType === 'google_sheets'
                    && (category.googleSheetsSource?.syncIntervalMinutes ?? 0) > 0 ? (
                    <div className={styles.mutedText}>
                      {t('dynamicTools.googleSheetsSyncScheduleLabel', {
                        ns: 'knowledge',
                        minutes: category.googleSheetsSource?.syncIntervalMinutes ?? 0,
                      })}
                    </div>
                  ) : null}
                  {category.dataSourceType === 'google_sheets'
                    && (category.googleSheetsSource?.syncIntervalMinutes ?? 0) <= 0 ? (
                    <div className={styles.mutedText}>
                      {t('dynamicTools.backgroundSyncDisabledLabel', { ns: 'knowledge', defaultValue: 'Background sync disabled (manual sync only)' })}
                    </div>
                  ) : null}
                  {category.googleSheetsSource?.lastSyncedAt ? (
                    <div className={styles.mutedText}>
                      {t('dynamicTools.googleSheetsSyncLastSyncedLabel', {
                        ns: 'knowledge',
                        date: new Date(category.googleSheetsSource.lastSyncedAt).toLocaleString(),
                      })}
                    </div>
                  ) : null}
                  {category.dataSourceType === 'consultation'
                    && (category.consultationSource?.syncIntervalMinutes ?? 0) > 0 ? (
                    <div className={styles.mutedText}>
                      {t('dynamicTools.consultationSyncScheduleLabel', {
                        ns: 'knowledge',
                        minutes: category.consultationSource?.syncIntervalMinutes ?? 0,
                      })}
                    </div>
                  ) : null}
                  {category.dataSourceType === 'consultation'
                    && (category.consultationSource?.syncIntervalMinutes ?? 0) <= 0 ? (
                    <div className={styles.mutedText}>
                      {t('dynamicTools.backgroundSyncDisabledLabel', { ns: 'knowledge', defaultValue: 'Background sync disabled (manual sync only)' })}
                    </div>
                  ) : null}
                  {category.externalSyncLastSyncedAt ? (
                    <div className={styles.mutedText}>
                      {t('dynamicTools.externalSyncLastSyncedLabel', {
                        ns: 'knowledge',
                        date: new Date(category.externalSyncLastSyncedAt).toLocaleString(),
                      })}
                    </div>
                  ) : null}
                  {category.consultationSource?.lastSyncedAt ? (
                    <div className={styles.mutedText}>
                      {t('dynamicTools.consultationSyncLastSyncedLabel', {
                        ns: 'knowledge',
                        date: new Date(category.consultationSource.lastSyncedAt).toLocaleString(),
                      })}
                    </div>
                  ) : null}
                </div>
                <div className={styles.dynamicToolsCardActions}>
                  {canManageKnowledge && category.dataSourceType === 'csv' ? (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => onChooseCategoryUpload(category.categoryId)}
                      disabled={uploadingCategoryId === category.categoryId}
                    >
                      {uploadingCategoryId === category.categoryId
                        ? t('dynamicTools.uploadingCategoryCsv', { ns: 'knowledge' })
                        : t('dynamicTools.uploadCategoryCsv', { ns: 'knowledge' })}
                    </button>
                  ) : canManageKnowledge && category.dataSourceType !== 'consultation' ? (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => onChooseCategoryUpload(category.categoryId)}
                      disabled={uploadingCategoryId === category.categoryId}
                    >
                      {uploadingCategoryId === category.categoryId
                        ? t('dynamicTools.uploadingCategoryJson', { ns: 'knowledge' })
                        : t('dynamicTools.uploadCategoryJson', { ns: 'knowledge' })}
                    </button>
                  ) : null}
                  {canSyncKnowledge && category.externalSyncUrl ? (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => void onSyncCategoryExternal(category)}
                      disabled={syncingCategoryId === category.categoryId}
                    >
                      {syncingCategoryId === category.categoryId
                        ? t('dynamicTools.syncingExternal', { ns: 'knowledge' })
                        : t(
                          category.dataSourceType === 'csv'
                            ? 'dynamicTools.syncExternalCsvNow'
                            : 'dynamicTools.syncExternalNow',
                          { ns: 'knowledge' },
                        )}
                    </button>
                  ) : null}
                  {canSyncKnowledge && category.dataSourceType === 'google_sheets' ? (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => void onSyncCategoryGoogleSheets(category)}
                      disabled={syncingCategoryId === category.categoryId}
                    >
                      {syncingCategoryId === category.categoryId
                        ? t('dynamicTools.syncingGoogleSheets', { ns: 'knowledge' })
                        : t('dynamicTools.syncGoogleSheetsNow', { ns: 'knowledge' })}
                    </button>
                  ) : null}
                  {canSyncKnowledge && category.dataSourceType === 'consultation' ? (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => void onSyncCategoryConsultation(category)}
                      disabled={syncingCategoryId === category.categoryId}
                    >
                      {syncingCategoryId === category.categoryId
                        ? t('dynamicTools.syncingConsultation', { ns: 'knowledge' })
                        : t('dynamicTools.syncConsultationNow', { ns: 'knowledge' })}
                    </button>
                  ) : null}
                  {canManageKnowledge ? (
                    <>
                      <button type="button" className="btn btn-secondary" onClick={() => openCreateTool(category.categoryId)}>
                        {t('dynamicTools.createTool', { ns: 'knowledge' })}
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={() => openEditCategory(category)}>
                        {t('edit', { ns: 'common', defaultValue: 'Edit' })}
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={() => void onDeleteCategory(category)}>
                        {t('delete', { ns: 'common' })}
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.emptyState}>{t('dynamicTools.noCategories', { ns: 'knowledge' })}</p>
        )}
      </section>
      </>
      ) : (
      <section className="company-section">
        <div className={styles.dynamicToolsSectionHeader}>
          <div>
            <h3>{t('dynamicTools.toolsListTitle', { ns: 'knowledge' })}</h3>
            <p className={styles.mutedText}>{t('dynamicTools.toolsBrowseHelp', { ns: 'knowledge' })}</p>
          </div>
          {canManageKnowledge ? <button
            type="button"
            className="btn btn-primary"
            onClick={() => openCreateTool()}
            disabled={categories.length === 0}
          >
            {t('dynamicTools.createTool', { ns: 'knowledge' })}
          </button> : null}
        </div>

        {categories.length === 0 ? (
          <p className={styles.emptyState}>{t('dynamicTools.createCategoryFirst', { ns: 'knowledge' })}</p>
        ) : tools.length > 0 ? (
          <div className={styles.dynamicToolsBrowseList}>
            {tools.map(tool => (
              <div key={tool.toolId} className={styles.dynamicToolsBrowseRow}>
                <div className={styles.dynamicToolsBrowseMain}>
                  <strong>
                    {tool.displayName}
                    {!tool.isPublished ? (
                      <span className={styles.dynamicToolsDraftBadge}>
                        {t('dynamicTools.draftBadge', { ns: 'knowledge' })}
                      </span>
                    ) : null}
                  </strong>
                  <div className={styles.mutedText}>{tool.kernelFunctionName}</div>
                  <div className={styles.mutedText}>
                    {getCategoryLabel(tool.categoryId)} · {tool.functionId}
                  </div>
                  <div className={styles.mutedText}>
                    {`${tool.parameters.length} ${t('dynamicTools.filterParameters', { ns: 'knowledge' }).toLowerCase()}`}
                    {tool.responseFields.length > 0
                      ? ` · ${tool.responseFields.length} ${t('dynamicTools.responseFieldsTitle', { ns: 'knowledge' }).toLowerCase()}`
                      : ` · ${t('dynamicTools.responseFieldsAll', { ns: 'knowledge' })}`}
                    {tool.resultMode !== 'list'
                      ? ` · ${t('dynamicTools.resultModeSummary', { ns: 'knowledge', mode: tool.resultMode })}`
                      : ''}
                    {tool.filterLogic === 'or'
                      ? ` · ${t('dynamicTools.filterLogicOrSummary', { ns: 'knowledge' })}`
                      : ''}
                  </div>
                </div>
                <div className={styles.dynamicToolsCardActions}>
                  <button type="button" className="btn btn-secondary" onClick={() => onEditTool(tool)}>
                    {t('edit', { ns: 'common', defaultValue: 'Edit' })}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => void onDeleteTool(tool)}>
                    {t('delete', { ns: 'common' })}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.emptyState}>{t('dynamicTools.noTools', { ns: 'knowledge' })}</p>
        )}
      </section>
      )}
    </div>
  );
};
